import './index.css'
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {Alert, Button, Divider, Dropdown, Input, MenuProps, message, Modal, Select, Space, Tag, Tooltip, TreeSelect} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  getHtmlCustomDomainPolicy,
  getId,
  reuploadFile,
  switchUploadRecordDomain,
  toggleHtmlShareCollection,
  updateUploadRecordRemark,
  updateHtmlCustomDomain
} from '../../helpers/file';
import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import QRCodeStyling, { Options as QRCodeStylingOptions } from 'qr-code-styling';
import {appendQuery, buildHttpLink, isRandomPath, replaceDomain} from "../../helpers/url";
import {convertImageToBase64, isImg} from "../../helpers/img";
import {recordImageError} from "../../helpers/imageError";
import {useImgCloudConfig} from "../../contexts/ImgCloudConfigContext";
import {
  copyToClipboard,
  getStoredWatermarkImageUrl,
  getStoredWatermarkText,
  saveWatermarkImageUrl,
  saveWatermarkText,
  urlSafeBase64Encode
} from '../../helpers/clipboard';
import Img from "../Img";
import {getDate, getTime} from '../../utils/dateUtils';
import {getFileType, getName, isZip} from '../../utils/fileTypeUtils';
import {isMufySupportedImage, isMufySupportedMediaDirectLink} from '../../utils/mufyLink';
import {getPreview} from '../../utils/fileIconMap';
import {useIsMobile} from '../../hooks/useIsMobile';
import {getIllicitReason} from '../../utils/illicitUtils';
import {getExpireTimeDisplay} from '../../utils/storageTypeUtils';
import ShortUrlModal from '../ShortUrlModal';
import ExpireTimeModal from '../ExpireTimeModal';
import PermanentSaveModal from '../PermanentSaveModal';
import {canApplyForPermanentSave, PermanentSaveSummary, permanentSaveStatusKey} from '../../services/permanentSave';
import EncryptedLinkModal from '../EncryptedLinkModal';
import SaleCreateModal from '../../pages/SaleLinks/SaleCreateModal';
import { saleRequest } from '../../services/saleLinks';
import { useSalePolicy } from '../../contexts/SalePolicyContext';
import { useSaleText } from '../../pages/SaleLinks/saleUi';
import TextEditModal from '../TextEditModal';
import { useTranslation } from '../../hooks/useTranslation';
import {
  canOverwriteReupload,
  hasMatchingOverwriteReuploadExtension,
  isOverwriteReuploadExtension
} from '../../utils/overwriteReupload';
import { canTextOnlineEdit } from '../../utils/textFileEdit';
import { isCustomDomainUploadRecord, isHtmlUploadRecord } from '../../utils/htmlRecord';
import { showUploadErrorModal } from '../../utils/uploadErrorModal';
import { ReuploadPolicy } from '../../types/reuploadPolicy';
import {buildDirTree, mapDirTreeTitles, sortDirectories} from '../../utils/dirTree';
import {loadUploadHostOptions, mergeHostOptions} from '../Header/Config';
import type {HostOption} from '../Header/Config';
import AiImageEditModal from '../AiImageEditModal';
import type {AiImageEditCapability} from '../../services/aiImageEdit';
import {
  HOME_ACTION_PARENTS,
  HomeActionKey,
} from '../../services/homeActionHighlight';
import {
  loadQrCodeOnlineLogoConfig,
  QrCodeOnlineLogoConfig,
  resolveQrCodeOnlineLogoUrl,
} from '../../services/qrCodeOnlineLogoConfig';

const safeDecode = (value = '') => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const isAllDirectory = (value?: string) => value === '__all__' || value === '全部目录';
const isDefaultDirectory = (value?: string) => !value || value === 'default' || value === '__default__' || value === '默认目录';
const MAX_REMARK_CODE_POINTS = 10;
const QR_CAPTION_DEFAULT_FONT_SIZE = 16;
const QR_CAPTION_EDIT_HINT_STORAGE_KEY = 'imgcloud_qr-caption-edit-hint-seen-v1';
const QR_CAPTION_EDIT_HINT_DURATION = 4000;
const QR_STYLE_SIZE = 1024;
const QR_LOGO_MAX_SIZE = 2 * 1024 * 1024;
const QR_LOGO_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
type QrCaptionAlignment = 'left' | 'center' | 'right';
type QrCaptionPosition = 'above' | 'below';
type QrStylePreset = 'classic' | 'ocean' | 'jade' | 'sunset' | 'graphite';
type QrDotsType = 'square' | 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'extra-rounded';
type QrCornerSquareType = 'square' | 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'extra-rounded';
type QrCornerDotType = 'square' | 'rounded' | 'dot' | 'dots' | 'classy' | 'classy-rounded' | 'extra-rounded';

interface QrCaptionSettings {
  text: string;
  color: string;
  fontSize: number;
  alignment: QrCaptionAlignment;
  position: QrCaptionPosition;
  hasCustomStyle: boolean;
}

interface QrStyleSettings {
  preset: QrStylePreset;
  foregroundColor: string;
  backgroundColor: string;
  gradientColor: string;
  useGradient: boolean;
  dotsType: QrDotsType;
  cornerSquareType: QrCornerSquareType;
  cornerDotType: QrCornerDotType;
  margin: number;
  logoUrl: string;
}

const getEmptyQrCodeOnlineLogoConfig = (): QrCodeOnlineLogoConfig => ({
  enabled: false,
  logoUrls: [],
});

const loadQrCodeOnlineLogoImage = (logoUrl: string) => new Promise<void>((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => resolve();
  image.onerror = () => reject(new Error('QR code online logo cannot be loaded'));
  image.src = logoUrl;
});

const QR_STYLE_PRESETS: Record<QrStylePreset, Omit<QrStyleSettings, 'preset' | 'logoUrl'>> = {
  classic: {
    foregroundColor: '#111827',
    backgroundColor: '#ffffff',
    gradientColor: '#111827',
    useGradient: false,
    dotsType: 'square',
    cornerSquareType: 'square',
    cornerDotType: 'square',
    margin: 16,
  },
  ocean: {
    foregroundColor: '#0f4c81',
    backgroundColor: '#ffffff',
    gradientColor: '#22a6d5',
    useGradient: true,
    dotsType: 'rounded',
    cornerSquareType: 'extra-rounded',
    cornerDotType: 'dot',
    margin: 18,
  },
  jade: {
    foregroundColor: '#0c6b5f',
    backgroundColor: '#f7fffc',
    gradientColor: '#29b391',
    useGradient: true,
    dotsType: 'dots',
    cornerSquareType: 'rounded',
    cornerDotType: 'rounded',
    margin: 20,
  },
  sunset: {
    foregroundColor: '#b54708',
    backgroundColor: '#fffaf5',
    gradientColor: '#f79009',
    useGradient: true,
    dotsType: 'classy-rounded',
    cornerSquareType: 'classy-rounded',
    cornerDotType: 'classy-rounded',
    margin: 18,
  },
  graphite: {
    foregroundColor: '#30343b',
    backgroundColor: '#f7f8fa',
    gradientColor: '#6b7280',
    useGradient: false,
    dotsType: 'extra-rounded',
    cornerSquareType: 'extra-rounded',
    cornerDotType: 'dot',
    margin: 20,
  },
};

const getDefaultQrStyle = (): QrStyleSettings => ({
  preset: 'classic',
  ...QR_STYLE_PRESETS.classic,
  logoUrl: '',
});

const sanitizeQrDownloadName = (value: string) => {
  return value.replace(/[\\/:*?"<>|]/g, '-').trim().slice(0, 80) || 'qrcode';
};

const QR_DOWNLOAD_CAPTION_SCALE = QR_STYLE_SIZE / 256;
const QR_DOWNLOAD_CAPTION_HORIZONTAL_PADDING = 64;
const QR_DOWNLOAD_CAPTION_VERTICAL_PADDING = 40;

const loadImageFromObjectUrl = (objectUrl: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('QR code image cannot be loaded'));
  image.src = objectUrl;
});

const wrapQrCaptionLines = (context: CanvasRenderingContext2D, text: string, maxWidth: number) => {
  return text.split(/\r?\n/).flatMap((paragraph) => {
    if (!paragraph) {
      return [''];
    }

    const lines: string[] = [];
    let line = '';
    Array.from(paragraph).forEach((character) => {
      const nextLine = `${line}${character}`;
      if (line && context.measureText(nextLine).width > maxWidth) {
        lines.push(line);
        line = character;
      } else {
        line = nextLine;
      }
    });
    if (line) {
      lines.push(line);
    }
    return lines;
  });
};

const canvasToPngBlob = (canvas: HTMLCanvasElement) => new Promise<Blob>((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) {
      resolve(blob);
    } else {
      reject(new Error('QR code image cannot be created'));
    }
  }, 'image/png');
});

const downloadPngBlob = (blob: Blob, name: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = `${name}.png`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

const limitCodePoints = (value: string, limit: number) => {
  return Array.from(value).slice(0, limit).join('');
};

const isAiEditableImage = (fileName = '', contentType = '', illicit?: number) => {
  if (illicit === 1) {
    return false;
  }
  const extension = getFileType('', fileName).toLowerCase();
  const normalizedContentType = contentType.toLowerCase();
  return ['png', 'jpg', 'jpeg'].includes(extension)
    && (!normalizedContentType || ['image/png', 'image/jpeg', 'image/jpg'].includes(normalizedContentType));
};

/**
 *
 * @author dongdong.fdd
 * @Company AutoNavi Co.,Ltd.
 * @date 2023/7/29 15:30
 */
// @ts-ignore
function UploadItem({dir, id, fileName, remark = '', time, url, rawUrl, contentType, size, illicit, illicitDetails, reviewStatus = undefined as string | undefined, reviewSource = undefined as string | undefined, reviewReason = undefined as string | undefined, permanentSaveRequest = undefined as PermanentSaveSummary | undefined, customDomain = '', storageType, expireTime, htmlShareEnabled = false, onDelete, onRefresh, userInfo, index, selected, onSelect, selectedIds, reuploadPolicy, aiEditCapability = undefined as AiImageEditCapability | undefined, selectable = true, highlightActionKeys = [] as HomeActionKey[]}) {
  const { t } = useTranslation();
  const saleText = useSaleText();
  const imgCloudConfig = useImgCloudConfig();
  const [saleModalOpen, setSaleModalOpen] = useState(false);
  const [linkMenuOpen, setLinkMenuOpen] = useState(false);
  const { policy: salePolicy } = useSalePolicy();
  const saleSource = useMemo(() => ({ id: Number(id), fileName, storageType, expireTime }), [id, fileName, storageType, expireTime]);
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [shortUrlModalOpen, setShortUrlModalOpen] = useState(false);
  const [shortUrlModalMode, setShortUrlModalMode] = useState<'short' | 'mufy'>('short');
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [permanentSaveOpen, setPermanentSaveOpen] = useState(false);
  const [permanentSaveState, setPermanentSaveState] = useState(permanentSaveRequest);
  useEffect(() => { setPermanentSaveState(permanentSaveRequest); }, [permanentSaveRequest]);
  const [remarkInput, setRemarkInput] = useState(String(remark || '').trim());
  const [remarkState, setRemarkState] = useState(String(remark || '').trim());
  const [remarkSaving, setRemarkSaving] = useState(false);
  const getDefaultQrCaption = (): QrCaptionSettings => ({
    text: remarkState || safeDecode(url),
    color: '#1677ff',
    fontSize: QR_CAPTION_DEFAULT_FONT_SIZE,
    alignment: 'left',
    position: 'above',
    hasCustomStyle: false,
  });
  const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);
  const [qrCaptionEditing, setQrCaptionEditing] = useState(false);
  const [qrCaption, setQrCaption] = useState<QrCaptionSettings>(getDefaultQrCaption);
  const qrCaptionSnapshotRef = useRef<QrCaptionSettings | null>(null);
  const [qrCaptionEditHintOpen, setQrCaptionEditHintOpen] = useState(false);
  const qrCaptionEditHintTimerRef = useRef<number | null>(null);
  const [qrStyleSettings, setQrStyleSettings] = useState<QrStyleSettings>(getDefaultQrStyle);
  const [qrStyleAdvancedOpen, setQrStyleAdvancedOpen] = useState(false);
  const qrStylingRef = useRef<QRCodeStyling | null>(null);
  const qrStylePreviewRef = useRef<HTMLDivElement>(null);
  const qrLogoInputRef = useRef<HTMLInputElement>(null);
  const qrLogoObjectUrlRef = useRef<string | null>(null);
  const [qrRemoteLogoConfig, setQrRemoteLogoConfig] = useState<QrCodeOnlineLogoConfig>(getEmptyQrCodeOnlineLogoConfig);
  const [qrRemoteLogoLoading, setQrRemoteLogoLoading] = useState(false);
  const [qrRemoteLogoFailedUrls, setQrRemoteLogoFailedUrls] = useState<string[]>([]);
  const qrRemoteLogoConfigRequestIdRef = useRef(0);
  const qrRemoteLogoSelectionRequestIdRef = useRef(0);
  const qrRemoteLogoSelectionPendingRef = useRef(false);
  const [mufyImageLongLinkHost, setMufyImageLongLinkHost] = useState('');
  const [mufyRecommendOverseasLongLink, setMufyRecommendOverseasLongLink] = useState(true);
  const [expireTimeModalOpen, setExpireTimeModalOpen] = useState(false);
  const [encryptedLinkModalOpen, setEncryptedLinkModalOpen] = useState(false);
  const [textEditModalOpen, setTextEditModalOpen] = useState(false);
  const [customDomainModalOpen, setCustomDomainModalOpen] = useState(false);
  const [customDomainPrefixInput, setCustomDomainPrefixInput] = useState('');
  const [customDomainSuffixes, setCustomDomainSuffixes] = useState<string[]>([]);
  const [selectedCustomDomainSuffix, setSelectedCustomDomainSuffix] = useState('');
  const [customDomainPolicyLoading, setCustomDomainPolicyLoading] = useState(false);
  const [customDomainSaving, setCustomDomainSaving] = useState(false);
  const [reuploading, setReuploading] = useState(false);
  const [htmlShareEnabledState, setHtmlShareEnabledState] = useState(Boolean(htmlShareEnabled));
  const [htmlShareSaving, setHtmlShareSaving] = useState(false);
  const [directoryMoveModalOpen, setDirectoryMoveModalOpen] = useState(false);
  const [directoryMoveLoading, setDirectoryMoveLoading] = useState(false);
  const [directoryListLoading, setDirectoryListLoading] = useState(false);
  const [directoryMoveTarget, setDirectoryMoveTarget] = useState('');
  const [directoryOptions, setDirectoryOptions] = useState<string[]>([]);
  const [domainSwitchModalOpen, setDomainSwitchModalOpen] = useState(false);
  const [domainSwitchOptions, setDomainSwitchOptions] = useState<HostOption[]>([]);
  const [domainSwitchTarget, setDomainSwitchTarget] = useState('');
  const [domainSwitchLoading, setDomainSwitchLoading] = useState(false);
  const [domainSwitchSaving, setDomainSwitchSaving] = useState(false);
  const [aiEditModalOpen, setAiEditModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const reuploadInputRef = useRef<HTMLInputElement>(null);
  
  // 判断是否是VIP用户
  const isVip = userInfo?.detail?.isActiveVip || userInfo?.isActiveVip || false;
  const isLoggedIn = Boolean(
    userInfo?.email ||
    userInfo?.username ||
    userInfo?.detail?.email ||
    userInfo?.detail?.username
  );

  // 获取违规原因
  const illicitReason = getIllicitReason(illicit, illicitDetails);
  const isTencentReviewPending = reviewSource === 'TENCENT_COS' && reviewStatus === 'PENDING';
  const canSelect = selectable && !isTencentReviewPending;
  const reviewDisplayReason = reviewStatus === 'REJECTED' && reviewReason
    ? reviewReason
    : illicitReason;
  const currentReuploadPolicy = reuploadPolicy as ReuploadPolicy | null;
  const canUseOverwriteReupload = canOverwriteReupload(fileName, storageType, currentReuploadPolicy);
  const hasOverwriteReuploadExtension = isOverwriteReuploadExtension(fileName, currentReuploadPolicy);
  const shouldShowOverwriteReupload = canUseOverwriteReupload || (!isLoggedIn && hasOverwriteReuploadExtension);
  const canUseTextOnlineEdit = canTextOnlineEdit(fileName, storageType);
  const isHtmlRecord = isHtmlUploadRecord(fileName, contentType);
  const canUseCustomDomain = isCustomDomainUploadRecord(fileName, contentType);
  const canUseHtmlVisualEdit = canUseTextOnlineEdit && isHtmlRecord;
  const canUseMufyShortLink = isMufySupportedImage(fileName, contentType);
  const canUseMufyDirectMediaLink = !canUseMufyShortLink && isMufySupportedMediaDirectLink(fileName, contentType);
  const canUseMufyLink = canUseMufyShortLink || canUseMufyDirectMediaLink;
  const canCreateEncryptedLink = isImg(url) || String(contentType || '').toLowerCase().startsWith('video/');
  const isVideoFile = String(contentType || '').toLowerCase().startsWith('video/');
  const displayFileName = safeDecode(getName(safeDecode(fileName)));
  const displayName = isTencentReviewPending
    ? t('uploadItem.reviewPending')
    : reviewDisplayReason || remarkState || displayFileName;
  const displayNameTitle = !reviewDisplayReason && !isTencentReviewPending && remarkState
    ? displayFileName
    : displayName;
  const reviewPendingInternationalHint = isTencentReviewPending ? (
    <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 400, flexShrink: 0 }}>
      {t('uploadItem.reviewPendingInternationalHintPrefix')}
      <a
        href="/international"
        style={{ color: '#1677ff', textDecoration: 'underline' }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          navigate('/international');
        }}
      >
        {t('uploadItem.reviewPendingInternationalLink')}
      </a>
      {t('uploadItem.reviewPendingInternationalHintSuffix')}
    </span>
  ) : null;
  const canUseAiEdit = Boolean(
    !isTencentReviewPending
    &&
    (aiEditCapability as AiImageEditCapability | undefined)?.enabled
    && isAiEditableImage(fileName, contentType, illicit)
  );
  const customDomainPrefixHasDot = customDomainPrefixInput.includes('.');
  const formatDirectoryLabel = (value: string) => {
    if (isAllDirectory(value)) return t('common.allDirectories');
    if (isDefaultDirectory(value)) return t('common.defaultDirectory');
    return value;
  };
  const directoryMoveTreeData = useMemo(() => {
    const candidates = directoryOptions.length > 0 ? directoryOptions : ['默认目录'];
    const tree = buildDirTree(candidates.filter((value) => !isAllDirectory(value)));
    return mapDirTreeTitles(tree, formatDirectoryLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directoryOptions, t]);
  const highlightedActionKeys = useMemo(() => {
    const availableActionKeys = new Set<HomeActionKey>([
      'link',
      'access',
      'qr-code',
      'short-url',
      'more',
      'more.remark',
      'more.move-directory',
    ]);

    if (isImg(url)) {
      availableActionKeys.add('link.original');
      availableActionKeys.add('link.compressed');
      availableActionKeys.add('link.overseas');
    }
    if (canCreateEncryptedLink) {
      availableActionKeys.add('link.encrypted');
    }
    if (isVideoFile) {
      availableActionKeys.add('link.video-to-audio');
    }
    if (shouldShowOverwriteReupload) {
      availableActionKeys.add('reupload');
    }
    if (canUseCustomDomain) {
      availableActionKeys.add('html-custom-domain');
    } else {
      availableActionKeys.add('more.markdown');
      if (canUseMufyLink) {
        availableActionKeys.add('mufy');
      }
    }
    if (isHtmlRecord) {
      availableActionKeys.add('more.html-share');
    }
    if (isZip(fileName)) {
      availableActionKeys.add('extract');
    }
    if (isImg(fileName)) {
      [
        'processing',
        'processing.format-encoding',
        'processing.base64',
        'processing.webp',
        'processing.thumbnails',
        'processing.thumbnail-100',
        'processing.thumbnail-200',
        'processing.thumbnail-300',
        'processing.thumbnail-600',
        'processing.watermarks',
        'processing.watermark-default',
        'processing.watermark-tile',
        'processing.watermark-southeast',
        'processing.image-watermark',
      ].forEach(actionKey => availableActionKeys.add(actionKey as HomeActionKey));
      if (canUseAiEdit) {
        availableActionKeys.add('processing.ai-edit');
      }
    }
    if (canUseTextOnlineEdit) {
      availableActionKeys.add('edit');
      if (canUseHtmlVisualEdit) {
        availableActionKeys.add('edit.source');
        availableActionKeys.add('edit.visual');
      }
    }
    if (isVip) {
      availableActionKeys.add('more.switch-domain');
    }
    if (userInfo.config?.imgViewer) {
      availableActionKeys.add('more.custom-preview');
    }

    const effectiveActionKeys = new Set<HomeActionKey>();
    highlightActionKeys.forEach((actionKey: HomeActionKey) => {
      if (!availableActionKeys.has(actionKey)) {
        return;
      }
      effectiveActionKeys.add(actionKey);
      (HOME_ACTION_PARENTS[actionKey] || []).forEach(parentKey => {
        if (availableActionKeys.has(parentKey)) {
          effectiveActionKeys.add(parentKey);
        }
      });
    });
    return effectiveActionKeys;
  }, [
    canUseAiEdit,
    canCreateEncryptedLink,
    canUseCustomDomain,
    canUseHtmlVisualEdit,
    canUseMufyLink,
    canUseTextOnlineEdit,
    fileName,
    highlightActionKeys,
    isHtmlRecord,
    isVideoFile,
    isVip,
    shouldShowOverwriteReupload,
    url,
    userInfo.config?.imgViewer,
  ]);
  const getActionClassName = (actionKey: HomeActionKey, baseClassName = '') => [
    baseClassName,
    highlightedActionKeys.has(actionKey) ? 'home-action-highlight' : '',
  ].filter(Boolean).join(' ');
  const renderActionLabel = (actionKey: HomeActionKey, label: React.ReactNode) =>
    highlightedActionKeys.has(actionKey)
      ? <span className="home-action-highlight">{label}</span>
      : label;

  useEffect(() => {
    setHtmlShareEnabledState(Boolean(htmlShareEnabled));
  }, [htmlShareEnabled]);

  useEffect(() => {
    const normalizedRemark = String(remark || '').trim();
    setRemarkState(normalizedRemark);
    setRemarkInput(normalizedRemark);
  }, [remark]);

  useEffect(() => () => {
    qrRemoteLogoConfigRequestIdRef.current += 1;
    qrRemoteLogoSelectionRequestIdRef.current += 1;
    qrRemoteLogoSelectionPendingRef.current = false;
    if (qrCaptionEditHintTimerRef.current !== null) {
      window.clearTimeout(qrCaptionEditHintTimerRef.current);
    }
    if (qrLogoObjectUrlRef.current) {
      URL.revokeObjectURL(qrLogoObjectUrlRef.current);
    }
  }, []);

  const qrStylingOptions = useMemo<QRCodeStylingOptions>(() => {
    const gradient = qrStyleSettings.useGradient ? {
      type: 'linear' as const,
      rotation: Math.PI / 4,
      colorStops: [
        { offset: 0, color: qrStyleSettings.foregroundColor },
        { offset: 1, color: qrStyleSettings.gradientColor },
      ],
    } : undefined;

    return {
      width: QR_STYLE_SIZE,
      height: QR_STYLE_SIZE,
      type: 'svg',
      data: url,
      margin: qrStyleSettings.margin,
      image: qrStyleSettings.logoUrl || undefined,
      qrOptions: {
        errorCorrectionLevel: qrStyleSettings.logoUrl ? 'H' : 'M',
      },
      imageOptions: {
        hideBackgroundDots: true,
        imageSize: 0.18,
        margin: 8,
        saveAsBlob: true,
        crossOrigin: 'anonymous',
      },
      dotsOptions: {
        type: qrStyleSettings.dotsType,
        color: qrStyleSettings.foregroundColor,
        gradient,
      },
      cornersSquareOptions: {
        type: qrStyleSettings.cornerSquareType,
        color: qrStyleSettings.foregroundColor,
        gradient,
      },
      cornersDotOptions: {
        type: qrStyleSettings.cornerDotType,
        color: qrStyleSettings.foregroundColor,
        gradient,
      },
      backgroundOptions: {
        color: qrStyleSettings.backgroundColor,
      },
    };
  }, [qrStyleSettings, url]);

  useEffect(() => {
    const preview = qrStylePreviewRef.current;
    if (!qrCodeModalOpen || !preview) {
      return;
    }

    if (!qrStylingRef.current) {
      qrStylingRef.current = new QRCodeStyling(qrStylingOptions);
    } else {
      qrStylingRef.current.update(qrStylingOptions);
    }

    if (!preview.hasChildNodes()) {
      qrStylingRef.current.append(preview);
    }
  }, [qrCodeModalOpen, qrStylingOptions]);

  const openRemarkModal = () => {
    setRemarkInput(remarkState);
    setRemarkModalOpen(true);
  };

  const submitRemark = async () => {
    if (!isVip) {
      return;
    }

    setRemarkSaving(true);
    try {
      const result = await updateUploadRecordRemark(
        id,
        getId() || localStorage.getItem('fdd_uid') || '',
        remarkInput
      );
      const normalizedRemark = String(result?.remark || '').trim();
      setRemarkState(normalizedRemark);
      setRemarkInput(normalizedRemark);
      setRemarkModalOpen(false);
      messageApi.open({
        type: 'success',
        content: t('uploadItem.remarkSaveSuccess'),
      });
      onRefresh && onRefresh();
    } catch (error: any) {
      messageApi.open({
        type: 'error',
        content: error?.message || t('uploadItem.remarkSaveFailed'),
      });
    } finally {
      setRemarkSaving(false);
    }
  };

  const handleImgLoadError = (e: any) => {
    console.info('e is', e);
    const img = e.target as HTMLImageElement;
    const currentSrc = img.src;
    
    // 检查当前URL是否包含参数
    const hasParams = currentSrc.includes('?');
    
    // 如果包含参数且还没有尝试过无参数版本，则尝试去掉参数重新加载
    if (hasParams && !img.dataset.retried) {
      const baseUrl = currentSrc.split('?')[0];
      console.info('图片加载失败，尝试去掉参数重新加载:', baseUrl);
      
      // 标记已经重试过，防止无限循环
      img.dataset.retried = 'true';
      img.src = baseUrl;
      return;
    }
    
    // 记录图片加载失败错误
    const imageUrl = currentSrc || 'unknown';
    const errorMessage = t('uploadItem.imageLoadFailed', { url: imageUrl });
    recordImageError(imageUrl, errorMessage);
    
    // 根据原始URL判断使用哪个兜底图片
    const fallbackImg = currentSrc.includes('autoupload/fr') 
      ? 'https://edgeone.51shazhu.com/autoupload/fdd/default/guoqi.svg'
      : 'https://z.wiki/autoupload/20230831/l32P.%E5%9B%BE%E7%89%87%E5%8A%A0%E8%BD%BD%E5%A4%B1%E8%B4%A5_%282%29.svg';
    
    // 设置为失败图片，并清空重试标记
    img.onerror = null;
    delete img.dataset.retried;
    img.src = fallbackImg;
  }

  // 处理图片链接的公共函数
  const handleImageProcess = (apiUrl: string, title: string) => {
    messageApi.info({
      content: t('uploadItem.processing'),
      key: 'imageProcessLoading'
    })
    fetch(apiUrl)
      .then(res => res.json())
      .then(data => {
        if (data.url) {
          Modal.info({
            title: t('uploadItem.processResult', { title }),
            content: (
              <div>
                <a href={data.url} target="_blank" rel="noopener noreferrer">{data.url}</a>
                <img 
                  src={`https://playground.z.wiki/qrcode/generateQRCode?data=${encodeURIComponent(data.url)}`}
                  alt="QR Code"
                  style={{ 
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
                <br />
                <br />
              </div>
            ),
            footer: (
              <div style={{float: 'right'}}>
                <Space>
                  <Button
                    type="primary"
                    onClick={() => {
                      copyToClipboard(data.url, t('uploadItem.processedLinkCopied'));
                      Modal.destroyAll();
                    }}
                  >
                    {t('uploadItem.copyLink')}
                  </Button>
                  <Button onClick={() => Modal.destroyAll()}>
                    {t('common.close')}
                  </Button>
                </Space>
              </div>
            )
          });
        } else {
          messageApi.open({
            type: 'error',
            content: t('uploadItem.processFailed'),
          });
        }
      })
      .catch(err => {
        messageApi.open({
          type: 'error',
          content: t('uploadItem.requestFailed', { message: err.message }),
        });
      });
  };

  const doDelete = async (hard: boolean, id: string, saleConfirmed = false) => {
    if (!saleConfirmed && userInfo?.username) {
      try {
        const affected = await saleRequest<number>(`/affected?uploadId=${Number(id)}`);
        if (affected > 0) {
          Modal.confirm({
            title: saleText('删除资源会影响售卖链接', 'Deleting this resource affects sale links'),
            content: saleText(`此资源关联 ${affected} 个售卖链接，删除后买家将无法通过这些入口访问。`, `${affected} sale links use this resource. Buyers will lose access through these entries.`),
            okText: saleText('继续删除', 'Delete resource'), cancelText: saleText('取消', 'Cancel'), okButtonProps: { danger: true },
            onOk: () => doDelete(hard, id, true),
          });
          return;
        }
      } catch {
        // Existing uploads must remain manageable before the optional sale migration is installed.
      }
    }
    console.info('onClick')
    messageApi.open({
      type: 'info',
      content: t('uploadItem.deleting'),
    });
    fetch(`/img/delete?hard=${hard}&id=${id}&uid=${getId()}`, {
      method: 'DELETE'
    }).then(res => res.json()).then((flag) => {
      if (flag) {
        messageApi.open({
          type: 'success',
          content: t('uploadItem.deleteSuccess')
        })
        onDelete && onDelete();
      } else {
        messageApi.open({
          type: 'error',
          content: t('uploadItem.deleteFailed')
        })
      }
    })
  }

  const getDefaultDirectoryMoveTarget = () => {
    if (isDefaultDirectory(dir) || isAllDirectory(dir)) {
      return '默认目录';
    }
    return dir;
  };

  const getCurrentUrlHost = () => {
    try {
      return new URL(url).host;
    } catch {
      return '';
    }
  };

  const getUserDomainOptions = () => {
    const domains = new Set<string>();
    const userDomains = Array.isArray(userInfo?.domains) ? userInfo.domains : [];
    userDomains
      .filter((domain: unknown): domain is string => typeof domain === 'string')
      .forEach((domain: string) => domains.add(domain));

    const configDomains = typeof userInfo?.config?.domains === 'string'
      ? userInfo.config.domains.split(',')
      : [];
    configDomains
      .map((domain: string) => domain.trim())
      .filter(Boolean)
      .forEach((domain: string) => domains.add(domain));

    return Array.from(domains);
  };

  const openDomainSwitchModal = async () => {
    if (!isVip) {
      messageApi.open({
        type: 'warning',
        content: t('uploadItem.switchDomainVipOnly'),
      });
      return;
    }

    setDomainSwitchModalOpen(true);
    setDomainSwitchTarget(getCurrentUrlHost());
    setDomainSwitchLoading(true);
    try {
      const globalOptions = await loadUploadHostOptions();
      const options = mergeHostOptions(globalOptions, getUserDomainOptions());
      setDomainSwitchOptions(options);

      const currentHost = getCurrentUrlHost();
      const defaultTarget = options.some((option) => option.value === currentHost)
        ? currentHost
        : options[0]?.value || '';
      setDomainSwitchTarget(defaultTarget);
      if (options.length === 0) {
        messageApi.open({
          type: 'warning',
          content: t('uploadItem.switchDomainNoOptions'),
        });
      }
    } catch (error: any) {
      messageApi.open({
        type: 'error',
        content: error?.message || t('uploadItem.switchDomainLoadFailed'),
      });
      setDomainSwitchOptions([]);
      setDomainSwitchTarget('');
    } finally {
      setDomainSwitchLoading(false);
    }
  };

  const submitDomainSwitch = async () => {
    if (!domainSwitchTarget) {
      messageApi.open({
        type: 'warning',
        content: t('uploadItem.switchDomainTargetRequired'),
      });
      return;
    }

    setDomainSwitchSaving(true);
    try {
      await switchUploadRecordDomain(id, domainSwitchTarget);
      messageApi.open({
        type: 'success',
        content: t('uploadItem.switchDomainSaveSuccess'),
      });
      setDomainSwitchModalOpen(false);
      onRefresh && onRefresh();
    } catch (error: any) {
      messageApi.open({
        type: 'error',
        content: error?.message || t('uploadItem.switchDomainSaveFailed'),
      });
    } finally {
      setDomainSwitchSaving(false);
    }
  };

  const fetchDirectoryOptions = async (preferredTarget = getDefaultDirectoryMoveTarget()) => {
    setDirectoryListLoading(true);
    try {
      const response = await fetch('/img-dir/list');
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || t('batchActions.getDirectoryFailed'));
      }

      const dirs = sortDirectories(Array.isArray(result.data) ? result.data : []);
      setDirectoryOptions(dirs);
      const nextTarget = preferredTarget && dirs.includes(preferredTarget) && !isAllDirectory(preferredTarget)
        ? preferredTarget
        : '默认目录';
      setDirectoryMoveTarget(nextTarget);
      return dirs;
    } catch (error: any) {
      messageApi.open({
        type: 'error',
        content: error?.message || t('batchActions.getDirectoryFailed'),
      });
      return [];
    } finally {
      setDirectoryListLoading(false);
    }
  };

  const openDirectoryMoveModal = () => {
    const defaultTarget = getDefaultDirectoryMoveTarget();
    setDirectoryMoveTarget(defaultTarget);
    setDirectoryMoveModalOpen(true);
    fetchDirectoryOptions(defaultTarget);
  };

  const submitDirectoryMove = async () => {
    if (!directoryMoveTarget) {
      messageApi.open({
        type: 'warning',
        content: t('batchActions.moveDirectoryTargetRequired'),
      });
      return;
    }
    if (isAllDirectory(directoryMoveTarget)) {
      messageApi.open({
        type: 'warning',
        content: t('batchActions.moveDirectoryAllNotAllowed'),
      });
      return;
    }

    const recordId = Number(id);
    if (!Number.isFinite(recordId)) {
      messageApi.open({
        type: 'error',
        content: t('common.failed'),
      });
      return;
    }

    setDirectoryMoveLoading(true);
    try {
      const response = await fetch('/img/batchMoveDirectory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids: [recordId],
          uid: getId() || localStorage.getItem('fdd_uid'),
          targetDir: directoryMoveTarget,
        }),
      });
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || t('batchActions.moveDirectoryFailedRetry'));
      }

      messageApi.open({
        type: 'success',
        content: result.message || t('batchActions.moveDirectorySuccess'),
      });
      setDirectoryMoveModalOpen(false);
      onRefresh && onRefresh();
    } catch (error: any) {
      messageApi.open({
        type: 'error',
        content: error?.message || t('batchActions.moveDirectoryFailedRetry'),
      });
    } finally {
      setDirectoryMoveLoading(false);
    }
  };

  const handleReuploadClick = () => {
    if (!isLoggedIn) {
      messageApi.open({
        type: 'warning',
        content: t('uploadItem.reuploadLoginRequired'),
      });
      return;
    }

    if (!currentReuploadPolicy?.temporaryAllowed && storageType === 'TEMPORARY') {
      messageApi.open({
        type: 'error',
        content: t('uploadItem.reuploadTemporaryNotSupported'),
      });
      return;
    }

    reuploadInputRef.current?.click();
  };

  const handleReuploadChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';

    if (!selectedFile) {
      return;
    }

    if (!currentReuploadPolicy?.temporaryAllowed && storageType === 'TEMPORARY') {
      messageApi.open({
        type: 'error',
        content: t('uploadItem.reuploadTemporaryNotSupported'),
      });
      return;
    }

    if (!canUseOverwriteReupload) {
      messageApi.open({
        type: 'error',
        content: t('uploadItem.reuploadUnsupportedType'),
      });
      return;
    }

    if (!hasMatchingOverwriteReuploadExtension(selectedFile.name, fileName, currentReuploadPolicy)) {
      messageApi.open({
        type: 'error',
        content: t('uploadItem.reuploadTypeMismatch'),
      });
      return;
    }

    const uploadMessageKey = `reupload-${id}`;
    let uploadFailed = false;

    setReuploading(true);

    await reuploadFile(
      id,
      selectedFile,
      (percent) => {
        let content = t('uploadItem.reuploadProgress', { percent });
        if (percent === 100) {
          content = t('upload.uploadComplete');
        }

        messageApi.open({
          type: 'loading',
          duration: 0,
          content,
          key: uploadMessageKey,
        });
      },
      (errorMessage, failedFileName, fileSize, httpStatus) => {
        uploadFailed = true;
        messageApi.destroy(uploadMessageKey);
        showUploadErrorModal(t, errorMessage, failedFileName, undefined, fileSize, httpStatus);
      }
    );

    setReuploading(false);

    if (uploadFailed) {
      return;
    }

    messageApi.open({
      type: 'success',
      content: t('uploadItem.reuploadSuccess'),
      key: uploadMessageKey,
      duration: 2,
    });
    onRefresh && onRefresh();
  };

  const getDomainParts = (domain = '', suffixes: string[] = []) => {
    const normalizedDomain = domain.toLowerCase();
    for (const suffix of suffixes) {
      const normalizedSuffix = suffix.toLowerCase().replace(/^\./, '');
      const suffixWithDot = `.${normalizedSuffix}`;
      if (normalizedSuffix && normalizedDomain.endsWith(suffixWithDot)) {
        return {
          prefix: normalizedDomain.slice(0, -suffixWithDot.length),
          suffix: normalizedSuffix,
        };
      }
    }
    return {
      prefix: '',
      suffix: suffixes[0] || '',
    };
  };

  const getCustomDomainHref = (domain = '') => {
    const value = domain.trim();
    if (!value) {
      return '';
    }
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
  };

  const openCustomDomainModal = async () => {
    setCustomDomainPolicyLoading(true);
    setCustomDomainModalOpen(true);
    try {
      const policy = await getHtmlCustomDomainPolicy();
      const suffixes = (policy?.domainSuffixes || [policy?.domainSuffix || policy?.defaultDomainSuffix])
        .filter(Boolean)
        .map((suffix: string) => suffix.replace(/^\./, ''));
      setCustomDomainSuffixes(suffixes);
      const domainParts = getDomainParts(customDomain || '', suffixes);
      setSelectedCustomDomainSuffix(domainParts.suffix);
      setCustomDomainPrefixInput(domainParts.prefix);
    } catch (error: any) {
      messageApi.open({
        type: 'error',
        content: error?.message || t('htmlCustomDomain.policyLoadFailed'),
      });
      setCustomDomainPrefixInput('');
    } finally {
      setCustomDomainPolicyLoading(false);
    }
  };

  const submitCustomDomain = async (prefixValue: string) => {
    if (!isVip) {
      messageApi.open({
        type: 'warning',
        content: t('htmlCustomDomain.vipOnly'),
      });
      return;
    }

    const normalizedPrefix = prefixValue.trim();
    if (normalizedPrefix.includes('.')) {
      messageApi.open({
        type: 'warning',
        content: t('htmlCustomDomain.prefixInvalid'),
      });
      return;
    }

    setCustomDomainSaving(true);
    try {
      await updateHtmlCustomDomain(id, normalizedPrefix, selectedCustomDomainSuffix);
      messageApi.open({
        type: 'success',
        content: normalizedPrefix
          ? t('htmlCustomDomain.saveSuccess')
          : t('htmlCustomDomain.clearSuccess'),
      });
      setCustomDomainModalOpen(false);
      onRefresh && onRefresh();
    } catch (error: any) {
      messageApi.open({
        type: 'error',
        content: error?.message || t('htmlCustomDomain.saveFailed'),
      });
    } finally {
      setCustomDomainSaving(false);
    }
  };

  const handleToggleHtmlShare = async () => {
    if (htmlShareSaving || !isHtmlRecord) {
      return;
    }

    const nextEnabled = !htmlShareEnabledState;
    setHtmlShareSaving(true);
    try {
      await toggleHtmlShareCollection(id, nextEnabled);
      setHtmlShareEnabledState(nextEnabled);
      messageApi.open({
        type: 'success',
        content: nextEnabled ? t('uploadItem.htmlShareAddSuccess') : t('uploadItem.htmlShareRemoveSuccess'),
      });
      onRefresh && onRefresh();
    } catch (error: any) {
      messageApi.open({
        type: 'error',
        content: error?.message || t('uploadItem.htmlShareToggleFailed'),
      });
    } finally {
      setHtmlShareSaving(false);
    }
  };

  const openMufyLink = async () => {
    try {
      const response = await fetch('/u/mufy/config');
      const config = await response.json();
      if (canUseMufyDirectMediaLink) {
        const mediaHost = String(config?.mediaHost || '').trim();
        try {
          const mufyDirectUrl = replaceDomain(url, mediaHost);
          copyToClipboard(mufyDirectUrl, t('mufyLink.linkCopied'));
        } catch {
          messageApi.open({
            type: 'error',
            content: t('mufyLink.generateFailed'),
          });
        }
        return;
      }
      if (!config?.enabled) {
        messageApi.open({
          type: 'info',
          content: t('mufyLink.developing'),
        });
        return;
      }
      setMufyImageLongLinkHost(String(config?.imageLongLinkHost || '').trim());
      setMufyRecommendOverseasLongLink(config?.recommendOverseasLongLink !== false);
      setShortUrlModalMode('mufy');
      setShortUrlModalOpen(true);
    } catch {
      messageApi.open({
        type: canUseMufyDirectMediaLink ? 'error' : 'info',
        content: canUseMufyDirectMediaLink ? t('mufyLink.generateFailed') : t('mufyLink.developing'),
      });
    }
  };

  const copyMufyImageLongLink = async () => {
    try {
      const response = await fetch('/u/mufy/config');
      const config = await response.json();
      if (!config?.enabled) {
        messageApi.open({
          type: 'info',
          content: t('mufyLink.developing'),
        });
        return;
      }

      try {
        const overseasLongUrl = replaceDomain(url, String(config?.imageLongLinkHost || '').trim());
        copyToClipboard(overseasLongUrl, t('mufyLink.overseasLongUrlCopied'));
      } catch {
        messageApi.open({
          type: 'error',
          content: t('mufyLink.generateFailed'),
        });
      }
    } catch {
      messageApi.open({
        type: 'error',
        content: t('mufyLink.generateFailed'),
      });
    }
  };

  const mufyImageLinkItems: MenuProps['items'] = [
    {
      key: 'copy-link',
      label: t('common.copyLink'),
      onClick: copyMufyImageLongLink,
    },
  ];

  const moreItems: MenuProps['items'] = [
    ...(isLoggedIn && (canApplyForPermanentSave(isLoggedIn, storageType) || permanentSaveState) ? [{
      key: 'permanent-save',
      label: t(permanentSaveStatusKey(permanentSaveState)),
      onClick: () => setPermanentSaveOpen(true),
    }] : []),
    {
      key: 'remark',
      label: renderActionLabel('more.remark', t('uploadItem.remark')),
      onClick() {
        openRemarkModal();
      },
    },
    {
      type: 'divider' as const,
    },
    // {
    //   key: 'soft-delete',
    //   label: '删除到回收站',
    //   onClick() {
    //     Modal.confirm({
    //       title: '删除到回收站',
    //       content: '确定要删除这个文件吗？文件将移动到回收站，7 天后自动清理。',
    //       okText: '确定',
    //       cancelText: '取消',
    //       onOk() {
    //         doDelete(false, id);
    //       },
    //     });
    //   }
    // },
    ...(isHtmlRecord ? [{
      key: 'html-share',
      label: renderActionLabel(
        'more.html-share',
        htmlShareEnabledState ? t('uploadItem.htmlShareRemove') : t('uploadItem.htmlShareAdd')
      ),
      disabled: htmlShareSaving,
      onClick() {
        handleToggleHtmlShare();
      },
    }, {
      type: 'divider' as const,
    }] : []),
    ...(!canUseCustomDomain ? [{
      key: 'markdown-link',
      label: renderActionLabel('more.markdown', 'MD'),
      onClick() {
        copyToClipboard(`![${getName(fileName)}](${url})`, t('common.markdownLinkCopied'));
      },
    }, {
      type: 'divider' as const,
    }] : []),
    {
      key: 'move-directory',
      label: renderActionLabel('more.move-directory', t('uploadItem.moveDirectory')),
      onClick() {
        openDirectoryMoveModal();
      },
    },
    ...(isVip ? [{
      key: 'switch-domain',
      label: renderActionLabel('more.switch-domain', t('uploadItem.switchDomain')),
      onClick() {
        openDomainSwitchModal();
      },
    }, {
      type: 'divider' as const,
    }] : []),
    {
      type: 'divider' as const,
    },
    {
      key: 'hard-delete',
      label: t('uploadItem.hardDelete'),
      danger: true,
      onClick() {
        Modal.confirm({
          title: t('uploadItem.hardDelete'),
          content: t('uploadItem.hardDeleteConfirm'),
          okText: t('uploadItem.hardDeleteOk'),
          cancelText: t('common.cancel'),
          okType: 'danger',
          onOk() {
            doDelete(true, id);
          },
        });
      }
    }
  ];

  if (userInfo.config?.imgViewer) {
    moreItems.push({
      key: 'custom-preview',
      label: renderActionLabel('more.custom-preview', t('uploadItem.customPreview')),
      onClick(row) {
        console.info('onClick ', row, rawUrl);
        copyToClipboard(`${userInfo.config?.imgViewer}?url=${rawUrl || url}`, t('uploadItem.customPreviewLinkCopied'));
      }
    })
  }

  const htmlEditItems: MenuProps['items'] = [
    {
      key: 'source',
      label: renderActionLabel('edit.source', t('textEdit.sourceEdit')),
      onClick() {
        setTextEditModalOpen(true);
      },
    },
    {
      key: 'visual',
      label: renderActionLabel('edit.visual', t('textEdit.visualEdit')),
      onClick() {
        navigate(`/web-hosting/editor/${id}`);
      },
    },
  ];

  // All resources share link actions; image transformations remain image-only.
  const getLinkItems: MenuProps['items'] = [
    {
      key: 'original',
      label: isImg(url) ? renderActionLabel('link.original', t('uploadItem.originalLink')) : t('common.copyLink'),
      onClick() {
        copyToClipboard(url, t(isImg(url) ? 'uploadItem.originalLinkCopied' : 'common.linkCopied'));
      }
    },
    ...(isImg(url) ? [{
      key: 'compressed',
      label: renderActionLabel('link.compressed', t('uploadItem.compressedLink')),
      onClick() {
        // 使用 /webp 参数进行无损压缩
        const [pathname, search] = url.split('?');
        const compressedUrl = search ? `${pathname}/webp?${search}` : `${pathname}/webp`;
        copyToClipboard(compressedUrl, t('uploadItem.compressedLinkCopied'));
      }
    },
    {
      key: 'overseas',
      label: renderActionLabel('link.overseas', t('uploadItem.overseasLink')),
      onClick() {
        // 使用海外CDN加速
        const urlObj = new URL(url);
        const overseasUrl = `https://xland.eu.org${urlObj.pathname}${urlObj.search}`;
        copyToClipboard(overseasUrl, t('uploadItem.overseasLinkCopied'));
      }
    },
    ] : []),
    ...(canCreateEncryptedLink ? [{
      type: 'divider' as const
    }, {
      key: 'encrypted',
      label: renderActionLabel('link.encrypted', t('uploadItem.encryptedLink')),
      onClick() {
        setEncryptedLinkModalOpen(true);
      }
    }] : []),
    ...(isVideoFile ? [{
      type: 'divider' as const
    }, {
      key: 'video-to-audio',
      label: renderActionLabel('link.video-to-audio', t('uploadItem.audioLink')),
      onClick() {
        navigate('/video-to-audio');
      }
    }] : []),
    ...(imgCloudConfig.httpLink?.enabled === true ? [{
      key: 'http',
      label: t('uploadItem.httpLink'),
      onClick() {
        copyToClipboard(buildHttpLink(url, imgCloudConfig.httpLink?.domain), t('uploadItem.httpLinkCopied'));
      }
    }] : []),
    { type: 'divider' },
    {
      key: 'sale-link',
      label: saleText('创建售卖链接', 'Create sale link'),
      disabled: !userInfo?.username || salePolicy?.creationEnabled !== true,
      onClick() { setSaleModalOpen(true); },
    },
  ];

  const formatProcessItems: MenuProps['items'] = [
    {
      key: 'base64',
      label: renderActionLabel('processing.base64', t('uploadItem.base64Format')),
      onClick() {
        let loadingMessage: (() => void) | null = null;
        
        convertImageToBase64(
          url,
          (base64String) => {
            // 成功回调
            copyToClipboard(base64String, t('uploadItem.base64Copied'));
          },
          (errorMessage) => {
            // 错误回调
            message.error(errorMessage);
          },
          (loading) => {
            // 加载状态回调
            if (loading) {
              loadingMessage = message.loading(t('uploadItem.convertingBase64'), 0);
            } else {
              loadingMessage?.();
            }
          }
        );
      }
    },
    {
      key: 'webp',
      label: renderActionLabel('processing.webp', t('uploadItem.webpFormat')),
      onClick() {
        // 直接处理字符串，在查询参数前添加 /webp
        const [pathname, search] = url.split('?');
        const webpUrl = search ? `${pathname}/webp?${search}` : `${pathname}/webp`;
        copyToClipboard(webpUrl, t('uploadItem.webpCopied'));
      }
    }
  ];

  const thumbnailProcessItems: MenuProps['items'] = [
    {
      key: 'thumbnail_100',
      label: renderActionLabel('processing.thumbnail-100', t('uploadItem.thumbnail100')),
      onClick() {
        const separator = url.includes('?') ? '&' : '?';
        const thumbnailUrl = `${url}${separator}imageMogr2/thumbnail/100x`;
        copyToClipboard(thumbnailUrl, t('uploadItem.thumbnail100Copied'));
      }
    },
    {
      key: 'thumbnail_200',
      label: renderActionLabel('processing.thumbnail-200', t('uploadItem.thumbnail200')),
      onClick() {
        const separator = url.includes('?') ? '&' : '?';
        const thumbnailUrl = `${url}${separator}imageMogr2/thumbnail/200x`;
        copyToClipboard(thumbnailUrl, t('uploadItem.thumbnail200Copied'));
      }
    },
    {
      key: 'thumbnail_300',
      label: renderActionLabel('processing.thumbnail-300', t('uploadItem.thumbnail300')),
      onClick() {
        const separator = url.includes('?') ? '&' : '?';
        const thumbnailUrl = `${url}${separator}imageMogr2/thumbnail/300x`;
        copyToClipboard(thumbnailUrl, t('uploadItem.thumbnail300Copied'));
      }
    },
    {
      key: 'thumbnail_600',
      label: renderActionLabel('processing.thumbnail-600', t('uploadItem.thumbnail600')),
      onClick() {
        const separator = url.includes('?') ? '&' : '?';
        const thumbnailUrl = `${url}${separator}imageMogr2/thumbnail/600x`;
        copyToClipboard(thumbnailUrl, t('uploadItem.thumbnail600Copied'));
      }
    }
  ];

  const watermarkProcessItems: MenuProps['items'] = [
    {
      key: 'watermark_default',
      label: renderActionLabel('processing.watermark-default', t('uploadItem.defaultWatermark')),
      onClick() {
        const storedText = getStoredWatermarkText();
        const customText = window.prompt(t('uploadItem.watermarkTextPrompt'), storedText);
        if (customText) {
          if (customText.length > 10) {
            message.error(t('uploadItem.watermarkTextTooLong'));
            return;
          }
          saveWatermarkText(customText);
          const separator = url.includes('?') ? '&' : '?';
          const text = urlSafeBase64Encode(customText);
          const watermarkUrl = `${url}${separator}watermark/2/text/${text}/fontsize/20/fill/IzAwMDAwMA/fontcolor/ffffff/t/50`;
          copyToClipboard(watermarkUrl, t('uploadItem.defaultWatermarkCopied'));
        }
      }
    },
    {
      key: 'watermark_tile',
      label: renderActionLabel('processing.watermark-tile', t('uploadItem.tileWatermark')),
      onClick() {
        const storedText = getStoredWatermarkText();
        const customText = window.prompt(t('uploadItem.watermarkTextPrompt'), storedText);
        if (customText) {
          if (customText.length > 10) {
            message.error(t('uploadItem.watermarkTextTooLong'));
            return;
          }
          saveWatermarkText(customText);
          const separator = url.includes('?') ? '&' : '?';
          const text = urlSafeBase64Encode(customText);
          const watermarkUrl = `${url}${separator}watermark/2/text/${text}/fontsize/20/fill/IzAwMDAwMA/fontcolor/ffffff/dissolve/10/gravity/center/batch/1/degree/30`;
          copyToClipboard(watermarkUrl, t('uploadItem.tileWatermarkCopied'));
        }
      }
    },
    {
      key: 'watermark_southeast',
      label: renderActionLabel('processing.watermark-southeast', t('uploadItem.southeastWatermark')),
      onClick() {
        const storedText = getStoredWatermarkText();
        const customText = window.prompt(t('uploadItem.watermarkTextPrompt'), storedText);
        if (customText) {
          if (customText.length > 10) {
            message.error(t('uploadItem.watermarkTextTooLong'));
            return;
          }
          saveWatermarkText(customText);
          const separator = url.includes('?') ? '&' : '?';
          const text = urlSafeBase64Encode(customText);
          const watermarkUrl = `${url}${separator}watermark/2/text/${text}/fontsize/16/fill/IzAwMDAwMA/fontcolor/ffffff/t/50/gravity/southeast`;
          copyToClipboard(watermarkUrl, t('body.southeastWatermarkCopied'));
        }
      }
    },
    {
      key: 'image_watermark',
      label: renderActionLabel('processing.image-watermark', t('uploadItem.imageWatermark')),
      onClick() {
        const storedImageUrl = getStoredWatermarkImageUrl();
        const watermarkImageUrl = window.prompt(t('uploadItem.watermarkImagePrompt'), storedImageUrl || 'https://z.wiki/autoupload/20230831/l32P.%E5%9B%BE%E7%89%87%E5%8A%A0%E8%BD%BD%E5%A4%B1%E8%B4%A5_%282%29.svg');
        if (watermarkImageUrl) {
          // 验证URL格式
          if (!watermarkImageUrl.startsWith('http://') && !watermarkImageUrl.startsWith('https://')) {
            message.error(t('uploadItem.watermarkImageInvalid'));
            return;
          }

          saveWatermarkImageUrl(watermarkImageUrl);
          
          // 从水印图片URL中截取路径
          let imageKey = '';
          try {
            const urlObj = new URL(watermarkImageUrl);
            // 提取路径部分，去掉域名
            imageKey = urlObj.pathname;
            // 如果路径以/开头，去掉开头的/
            if (imageKey.startsWith('/')) {
              imageKey = imageKey.substring(1);
            }
          } catch (e) {
            message.error(t('uploadItem.watermarkImageUrlError'));
            return;
          }
          
          const separator = url.includes('?') ? '&' : '?';
          const encodedImageKey = urlSafeBase64Encode(imageKey);
          // 图片水印参数：1表示图片水印，image_key/编码后的路径，gravity位置，ws宽度，ht高度
          const watermarkUrl = `${url}${separator}watermark/1/image_key/${encodedImageKey}/gravity/southeast/ws/100/ht/50`;
          copyToClipboard(watermarkUrl, t('body.imageWatermarkCopied'));
        }
      }
    }
  ];

  // Keep the first level short while preserving every existing processing action.
  const photoProcessItems: MenuProps['items'] = [
    ...(canUseAiEdit ? [{
      key: 'ai-edit',
      label: renderActionLabel('processing.ai-edit', (
        <Space size={6}>
          <EditOutlined />
          <span>{t('aiImageEdit.menuLabel')}</span>
          <Tag color="gold" style={{ marginInlineEnd: 0 }}>VIP</Tag>
        </Space>
      )),
      onClick() {
        setAiEditModalOpen(true);
      },
    }, {
      type: 'divider' as const,
    }] : []),
    {
      key: 'format-encoding',
      label: renderActionLabel(
        'processing.format-encoding',
        t('uploadItem.processingFormatEncoding')
      ),
      children: formatProcessItems,
    },
    {
      key: 'thumbnails',
      label: renderActionLabel('processing.thumbnails', t('uploadItem.processingThumbnails')),
      children: thumbnailProcessItems,
    },
    {
      key: 'watermarks',
      label: renderActionLabel('processing.watermarks', t('uploadItem.processingWatermarks')),
      children: watermarkProcessItems,
    },
  ];

  const openQrCodeModal = () => {
    setQrCaption(getDefaultQrCaption());
    if (qrLogoObjectUrlRef.current) {
      URL.revokeObjectURL(qrLogoObjectUrlRef.current);
      qrLogoObjectUrlRef.current = null;
    }
    setQrStyleSettings(getDefaultQrStyle());
    setQrStyleAdvancedOpen(false);
    setQrRemoteLogoConfig(getEmptyQrCodeOnlineLogoConfig());
    setQrRemoteLogoFailedUrls([]);
    qrRemoteLogoSelectionPendingRef.current = false;
    qrCaptionSnapshotRef.current = null;
    setQrCaptionEditing(false);
    setQrCodeModalOpen(true);
    void loadQrRemoteLogos();

    try {
      if (window.localStorage.getItem(QR_CAPTION_EDIT_HINT_STORAGE_KEY)) {
        return;
      }
      window.localStorage.setItem(QR_CAPTION_EDIT_HINT_STORAGE_KEY, 'true');
    } catch {
      // Keep the hint available for this session when local storage is unavailable.
    }

    setQrCaptionEditHintOpen(true);
    if (qrCaptionEditHintTimerRef.current !== null) {
      window.clearTimeout(qrCaptionEditHintTimerRef.current);
    }
    qrCaptionEditHintTimerRef.current = window.setTimeout(() => {
      setQrCaptionEditHintOpen(false);
      qrCaptionEditHintTimerRef.current = null;
    }, QR_CAPTION_EDIT_HINT_DURATION);
  };

  const closeQrCodeModal = () => {
    qrRemoteLogoConfigRequestIdRef.current += 1;
    qrRemoteLogoSelectionRequestIdRef.current += 1;
    qrRemoteLogoSelectionPendingRef.current = false;
    qrCaptionSnapshotRef.current = null;
    setQrCaptionEditing(false);
    setQrCodeModalOpen(false);
    if (qrLogoObjectUrlRef.current) {
      URL.revokeObjectURL(qrLogoObjectUrlRef.current);
      qrLogoObjectUrlRef.current = null;
    }
    setQrStyleSettings(getDefaultQrStyle());
    setQrStyleAdvancedOpen(false);
    setQrRemoteLogoConfig(getEmptyQrCodeOnlineLogoConfig());
    setQrRemoteLogoLoading(false);
    setQrRemoteLogoFailedUrls([]);
    setQrCaptionEditHintOpen(false);
    if (qrCaptionEditHintTimerRef.current !== null) {
      window.clearTimeout(qrCaptionEditHintTimerRef.current);
      qrCaptionEditHintTimerRef.current = null;
    }
  };

  const startQrCaptionEditing = () => {
    qrCaptionSnapshotRef.current = {...qrCaption};
    setQrCaptionEditing(true);
  };

  const cancelQrCaptionEditing = () => {
    if (qrCaptionSnapshotRef.current) {
      setQrCaption(qrCaptionSnapshotRef.current);
    }
    qrCaptionSnapshotRef.current = null;
    setQrCaptionEditing(false);
  };

  const finishQrCaptionEditing = () => {
    qrCaptionSnapshotRef.current = null;
    setQrCaptionEditing(false);
  };

  const applyQrStylePreset = (preset: QrStylePreset) => {
    setQrStyleSettings((current) => ({
      preset,
      ...QR_STYLE_PRESETS[preset],
      logoUrl: current.logoUrl,
    }));
  };

  const loadQrRemoteLogos = async () => {
    const requestId = qrRemoteLogoConfigRequestIdRef.current + 1;
    qrRemoteLogoConfigRequestIdRef.current = requestId;
    setQrRemoteLogoLoading(true);
    try {
      const config = await loadQrCodeOnlineLogoConfig();
      if (qrRemoteLogoConfigRequestIdRef.current !== requestId) {
        return;
      }
      setQrRemoteLogoConfig(config);
    } catch {
      if (qrRemoteLogoConfigRequestIdRef.current === requestId) {
        setQrRemoteLogoConfig(getEmptyQrCodeOnlineLogoConfig());
      }
    } finally {
      if (qrRemoteLogoConfigRequestIdRef.current === requestId) {
        setQrRemoteLogoLoading(false);
      }
    }
  };

  const handleQrLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!QR_LOGO_ACCEPTED_TYPES.includes(file.type)) {
      messageApi.error(t('uploadItem.qrCodeLogoTypeInvalid'));
      return;
    }
    if (file.size > QR_LOGO_MAX_SIZE) {
      messageApi.error(t('uploadItem.qrCodeLogoTooLarge'));
      return;
    }

    try {
      const logoUrl = URL.createObjectURL(file);
      if (qrLogoObjectUrlRef.current) {
        URL.revokeObjectURL(qrLogoObjectUrlRef.current);
      }
      qrLogoObjectUrlRef.current = logoUrl;
      setQrStyleSettings((current) => ({...current, logoUrl}));
    } catch {
      messageApi.error(t('uploadItem.qrCodeLogoReadFailed'));
    }
  };

  const removeQrLogo = () => {
    if (qrLogoObjectUrlRef.current) {
      URL.revokeObjectURL(qrLogoObjectUrlRef.current);
      qrLogoObjectUrlRef.current = null;
    }
    setQrStyleSettings((current) => ({...current, logoUrl: ''}));
  };

  const selectQrRemoteLogo = async (logoUrl: string) => {
    if (qrRemoteLogoSelectionPendingRef.current) {
      return;
    }
    const requestId = qrRemoteLogoSelectionRequestIdRef.current + 1;
    qrRemoteLogoSelectionRequestIdRef.current = requestId;
    qrRemoteLogoSelectionPendingRef.current = true;
    try {
      await loadQrCodeOnlineLogoImage(logoUrl);
      if (qrRemoteLogoSelectionRequestIdRef.current !== requestId) {
        return;
      }
      if (qrLogoObjectUrlRef.current) {
        URL.revokeObjectURL(qrLogoObjectUrlRef.current);
        qrLogoObjectUrlRef.current = null;
      }
      setQrStyleSettings((current) => ({...current, logoUrl}));
    } catch {
      if (qrRemoteLogoSelectionRequestIdRef.current === requestId) {
        setQrRemoteLogoFailedUrls((current) => current.includes(logoUrl) ? current : [...current, logoUrl]);
        messageApi.error(t('uploadItem.qrCodeOnlineLogoSelectFailed'));
      }
    } finally {
      if (qrRemoteLogoSelectionRequestIdRef.current === requestId) {
        qrRemoteLogoSelectionPendingRef.current = false;
      }
    }
  };

  const downloadStyledQrCode = async () => {
    try {
      // qr-code-styling caches raster output, so export from a fresh instance on every download.
      const qrStyling = new QRCodeStyling(qrStylingOptions);
      const downloadName = `${sanitizeQrDownloadName(displayFileName)}-qrcode`;
      const captionText = qrCaption.text.trim();
      if (!captionText) {
        await qrStyling.download({
          name: downloadName,
          extension: 'png',
        });
        messageApi.success(t('uploadItem.qrCodeDownloadSuccess'));
        return;
      }

      const qrCodeBlob = await qrStyling.getRawData('png');
      if (!(qrCodeBlob instanceof Blob)) {
        throw new Error('QR code image is unavailable');
      }

      const qrCodeObjectUrl = URL.createObjectURL(qrCodeBlob);
      try {
        const qrCodeImage = await loadImageFromObjectUrl(qrCodeObjectUrl);
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('QR code canvas is unavailable');
        }

        const fontSize = qrCaption.fontSize * QR_DOWNLOAD_CAPTION_SCALE;
        const lineHeight = Math.ceil(fontSize * 1.5);
        context.font = `${fontSize}px sans-serif`;
        const captionLines = wrapQrCaptionLines(
          context,
          captionText,
          QR_STYLE_SIZE - QR_DOWNLOAD_CAPTION_HORIZONTAL_PADDING * 2
        );
        const captionHeight = QR_DOWNLOAD_CAPTION_VERTICAL_PADDING * 2 + lineHeight * captionLines.length;
        canvas.width = QR_STYLE_SIZE;
        canvas.height = QR_STYLE_SIZE + captionHeight;

        context.fillStyle = qrStyleSettings.backgroundColor;
        context.fillRect(0, 0, canvas.width, canvas.height);
        const qrCodeY = qrCaption.position === 'above' ? captionHeight : 0;
        context.drawImage(qrCodeImage, 0, qrCodeY, QR_STYLE_SIZE, QR_STYLE_SIZE);

        context.fillStyle = qrCaption.color;
        context.font = `${fontSize}px sans-serif`;
        context.textBaseline = 'top';
        context.textAlign = qrCaption.alignment;
        const captionX = qrCaption.alignment === 'left'
          ? QR_DOWNLOAD_CAPTION_HORIZONTAL_PADDING
          : qrCaption.alignment === 'right'
            ? QR_STYLE_SIZE - QR_DOWNLOAD_CAPTION_HORIZONTAL_PADDING
            : QR_STYLE_SIZE / 2;
        const captionY = (qrCaption.position === 'above' ? 0 : QR_STYLE_SIZE)
          + QR_DOWNLOAD_CAPTION_VERTICAL_PADDING;
        captionLines.forEach((line, index) => {
          context.fillText(line, captionX, captionY + lineHeight * index);
        });

        downloadPngBlob(await canvasToPngBlob(canvas), downloadName);
      } finally {
        URL.revokeObjectURL(qrCodeObjectUrl);
      }
      messageApi.success(t('uploadItem.qrCodeDownloadSuccess'));
    } catch {
      messageApi.error(t('uploadItem.qrCodeDownloadFailed'));
    }
  };

  const renderQrCaption = () => (
    <div
      className={`qr-code-caption-slot qr-code-caption-slot--${qrCaption.position}`}
      data-testid="qr-code-caption-slot"
    >
      {qrCaptionEditing ? (
        <div className="qr-code-inline-editor">
          <Input
            aria-label={t('uploadItem.qrCodeCaption')}
            autoFocus
            value={qrCaption.text}
            style={qrCaption.hasCustomStyle ? {
              color: qrCaption.color,
              fontSize: `${qrCaption.fontSize}px`,
              textAlign: qrCaption.alignment,
            } : undefined}
            onChange={(event) => setQrCaption((current) => ({
              ...current,
              text: event.target.value,
            }))}
            placeholder={t('uploadItem.qrCodeCaptionPlaceholder')}
          />
          <div className="qr-code-editor-toolbar">
            <input
              aria-label={t('uploadItem.qrCodeColor')}
              type="color"
              value={qrCaption.color}
              onChange={(event) => setQrCaption((current) => ({
                ...current,
                color: event.target.value,
                hasCustomStyle: true,
              }))}
            />
            <Select
              aria-label={t('uploadItem.qrCodeFontSize')}
              value={qrCaption.fontSize}
              onChange={(fontSize) => setQrCaption((current) => ({
                ...current,
                fontSize,
                hasCustomStyle: true,
              }))}
              options={[14, 16, 18, 20, 24, 28].map((fontSize) => ({
                value: fontSize,
                label: `${fontSize}px`,
              }))}
            />
            <div className="qr-code-alignment-controls" role="group" aria-label={t('uploadItem.qrCodeAlignment')}>
              <Button
                aria-label={t('uploadItem.qrCodeAlignLeft')}
                type={qrCaption.alignment === 'left' ? 'primary' : 'default'}
                icon={<AlignLeftOutlined />}
                onClick={() => setQrCaption((current) => ({...current, alignment: 'left', hasCustomStyle: true}))}
              />
              <Button
                aria-label={t('uploadItem.qrCodeAlignCenter')}
                type={qrCaption.alignment === 'center' ? 'primary' : 'default'}
                icon={<AlignCenterOutlined />}
                onClick={() => setQrCaption((current) => ({...current, alignment: 'center', hasCustomStyle: true}))}
              />
              <Button
                aria-label={t('uploadItem.qrCodeAlignRight')}
                type={qrCaption.alignment === 'right' ? 'primary' : 'default'}
                icon={<AlignRightOutlined />}
                onClick={() => setQrCaption((current) => ({...current, alignment: 'right', hasCustomStyle: true}))}
              />
            </div>
            <Button
              className="qr-code-editor-done"
              size="small"
              type="primary"
              onClick={finishQrCaptionEditing}
            >
              {t('uploadItem.qrCodeEditDone')}
            </Button>
          </div>
        </div>
      ) : (
        <p
          className="qr-code-caption-preview"
          style={qrCaption.hasCustomStyle ? {
            color: qrCaption.color,
            fontSize: `${qrCaption.fontSize}px`,
            textAlign: qrCaption.alignment,
          } : undefined}
        >
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={qrCaption.hasCustomStyle ? {color: 'inherit'} : undefined}
          >
            {qrCaption.text || t('uploadItem.qrCodeCaptionEmpty')}
          </a>
          <Tooltip title={t('uploadItem.qrCodeEditHint')} open={qrCaptionEditHintOpen} placement="top">
            <Button
              aria-label={t('uploadItem.qrCodeEdit')}
              className={`qr-code-caption-edit-trigger${qrCaptionEditHintOpen ? ' qr-code-caption-edit-hint-visible' : ''}`}
              type="text"
              icon={<EditOutlined />}
              onClick={startQrCaptionEditing}
            />
          </Tooltip>
        </p>
      )}
    </div>
  );

  return (
    <div
      className="UploadItem"
      style={{
        width: isMobile ? '100%' : '324px',
        marginBottom: isMobile ? '12px' : '28px',
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        overflow: 'hidden',
        position: 'relative',
        ...(isMobile ? { border: '1px solid #f0f0f0' } : {})
      }}
    >
      {/* Selection Checkbox */}
      {canSelect && (
        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={!!selected}
            onChange={e => onSelect && onSelect(id, e.target.checked)}
            className="upload-item-checkbox"
            style={{
              width: 18,
              height: 18,
            }}
            title={t('uploadItem.selectThis')}
          />
          {/* 序号展示 */}
          {selected && selectedIds && selectedIds.includes(id) && (
            <span style={{ color: '#2095f2', fontWeight: 600, marginLeft: 4, fontSize: 13 }}>
              {selectedIds.indexOf(id) + 1}
            </span>
          )}
        </div>
      )}
      {contextHolder}
      
      {/* 桌面版显示大图 */}
      {!isMobile && isTencentReviewPending && (
        <div
          className="img"
          aria-label={t('uploadItem.reviewPending')}
          style={{
            height: '188px',
            width: '100%',
            background: 'linear-gradient(145deg, #fff7e6 0%, #fff1d6 100%)',
            color: '#ad6800',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
          }}
        >
          <LoadingOutlined style={{ fontSize: '34px' }} />
          <span style={{ fontSize: '14px', fontWeight: 500 }}>{t('uploadItem.reviewPending')}</span>
        </div>
      )}
      {!isMobile && !isTencentReviewPending && (
        <img
          className="img"
          src={illicit === 1 ? 'https://a.tuchuangyun.top/autoupload/fdd/default/%E8%BF%9D%E7%A6%81.svg' : (isImg(url) ? (isRandomPath(url) ? appendQuery(url, "imageMogr2/thumbnail/700x"): appendQuery(url, `id=` + id)) : getPreview(url))}
          onError={handleImgLoadError}
          loading={'lazy'}
          onClick={() => canSelect && onSelect && onSelect(id, !selected)}
          alt={displayFileName}
          style={{
            height: '188px',
            width: '100%',
            backgroundRepeat: 'no-repeat',
            objectFit: "fill",
            backgroundSize: "unset",
            backgroundPosition: "center center",
            padding: "15px",
            boxSizing: "border-box",
            cursor: canSelect ? 'pointer' : 'default',
          }}
        />
      )}
      <div
        className="summary"
        style={{
          paddingTop: '12px',
          paddingLeft: '14px',
          paddingRight: '14px',
          paddingBottom: '20px',
        }}
      >
        {isMobile ? (
          // 移动端布局：图片在H3前面
          <div className="info" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            {isTencentReviewPending ? (
              <div
                className="wap-thumbnail"
                aria-label={t('uploadItem.reviewPending')}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '4px',
                  flexShrink: 0,
                  color: '#ad6800',
                  background: '#fff7e6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LoadingOutlined style={{ fontSize: '22px' }} />
              </div>
            ) : (
              <Img
                className="wap-thumbnail"
                src={illicit === 1 ? 'https://a.tuchuangyun.top/autoupload/fdd/default/%E8%BF%9D%E7%A6%81.svg' : (isImg(url) ? (isRandomPath(url) ? appendQuery(url, "imageMogr2/thumbnail/100x") : appendQuery(url, `id=` + id)) : getPreview(url))}
                onError={handleImgLoadError}
                showPreview={isImg(url) && illicit !== 1}
                loading={'lazy'}
                previewSize='original'
                onClick={() => canSelect && onSelect && onSelect(id, !selected)}
                alt={displayFileName}
                style={{
                  width: '48px',
                  height: '48px',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  flexShrink: 0,
                  cursor: canSelect ? 'pointer' : 'default',
                }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3
                className="file-name"
                style={{
                  direction: isTencentReviewPending || (!reviewDisplayReason && remarkState) ? "ltr" : "rtl",
                  paddingBottom: '4px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  margin: 0,
                  "height": "20px",
                  "fontFamily": "PingFangSC",
                  "fontWeight": 400,
                  "fontSize": "14px",
                  "color": reviewDisplayReason ? "#ff4d4f" : isTencentReviewPending ? '#ad6800' : "#21242E",
                  "letterSpacing": "0",
                  textAlign: "left",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={displayNameTitle}
              >
                <span style={{ flex: isTencentReviewPending ? '0 0 auto' : 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {displayName}
                </span>
                {reviewPendingInternationalHint}
                {reviewDisplayReason && (
                  <Tooltip title={t('error.contactOnError')}>
                    <QuestionCircleOutlined style={{ fontSize: '12px', color: '#ff4d4f', cursor: 'help', flexShrink: 0 }} />
                  </Tooltip>
                )}
              </h3>
              <div
                className="text-info"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  "fontFamily": "PingFangSC",
                  "fontWeight": 400,
                  "fontSize": "12px",
                  "color": "#757D8B",
                  "letterSpacing": "0"
                }}
              >
                <div style={{"display": "flex", "justifyContent": "space-between"}}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {storageType ? (
                      <>
                        <span style={{ color: storageType === 'TEMPORARY' ? '#ff4d4f' : 'inherit' }}>
                          {permanentSaveState?.reviewStatus === 'APPROVED' && permanentSaveState.executionStatus === 'SUCCESS'
                            ? t('permanentSave.status.SUCCESS') : getExpireTimeDisplay(storageType, expireTime)}
                        </span>
                        {storageType === 'TEMPORARY' && (
                          <Tooltip title={t('common.temporaryStorageHint')}>
                            <InfoCircleOutlined style={{ fontSize: '12px', color: '#ff4d4f', cursor: 'help' }} />
                          </Tooltip>
                        )}
                        {storageType === 'LONG_TERM' && permanentSaveState?.reviewStatus !== 'APPROVED' && (
                          <Tooltip title={t('common.clickToSetExpireTime')}>
                            <EditOutlined 
                              style={{ fontSize: '12px', color: '#1890ff', cursor: 'pointer' }} 
                              onClick={() => !isTencentReviewPending && setExpireTimeModalOpen(true)}
                            />
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      `${t('common.format')}：${getFileType(contentType, fileName)}`
                    )}
                  </span>
                  <span>{t('common.size')}：{size || '-'}</span>
                </div>
                <div style={{"display": "flex", "justifyContent": "space-between"}}><span>{getDate(new Date(time))} {getTime(new Date(time))}</span>  <span>{dir == 'default' ? t('common.defaultDirectory') : dir}</span></div>
              </div>
            </div>
          </div>
        ) : (
          // 桌面端布局：传统布局
          <div className="info">
            <h3
              className="file-name"
              style={{
                direction: isTencentReviewPending || (!reviewDisplayReason && remarkState) ? "ltr" : "rtl",
                paddingBottom: '7px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                margin: 0,
                "height": "22px",
                "fontFamily": "PingFangSC",
                "fontWeight": 400,
                "fontSize": "16px",
                "color": reviewDisplayReason ? "#ff4d4f" : isTencentReviewPending ? '#ad6800' : "#21242E",
                "letterSpacing": "0",
                textAlign: "left",
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title={displayNameTitle}
            >
              <span style={{ flex: isTencentReviewPending ? '0 0 auto' : 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {displayName}
              </span>
              {reviewPendingInternationalHint}
              {reviewDisplayReason && (
                <Tooltip title={t('error.contactOnError')}>
                  <QuestionCircleOutlined style={{ fontSize: '14px', color: '#ff4d4f', cursor: 'help', flexShrink: 0 }} />
                </Tooltip>
              )}
            </h3>
            <span
              className="text-info"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                "height": "22px",
                "fontFamily": "PingFangSC",
                "fontWeight": 400,
                "fontSize": "16px",
                "color": "#757D8B",
                "letterSpacing": "0"
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {storageType ? (
                  <>
                    <span style={{ color: storageType === 'TEMPORARY' ? '#ff4d4f' : 'inherit' }}>
                      {permanentSaveState?.reviewStatus === 'APPROVED' && permanentSaveState.executionStatus === 'SUCCESS'
                        ? t('permanentSave.status.SUCCESS') : getExpireTimeDisplay(storageType, expireTime)}
                    </span>
                    {storageType === 'TEMPORARY' && (
                      <Tooltip title={t('common.temporaryStorageHint')}>
                        <InfoCircleOutlined style={{ fontSize: '14px', color: '#ff4d4f', cursor: 'help' }} />
                      </Tooltip>
                    )}
                    {storageType === 'LONG_TERM' && permanentSaveState?.reviewStatus !== 'APPROVED' && (
                      <Tooltip title={t('common.clickToSetExpireTime')}>
                        <EditOutlined 
                          style={{ fontSize: '14px', color: '#1890ff', cursor: 'pointer' }} 
                              onClick={() => !isTencentReviewPending && setExpireTimeModalOpen(true)}
                        />
                      </Tooltip>
                    )}
                  </>
                ) : (
                  `${t('common.format')}：${getFileType(contentType, fileName)}`
                )}
              </span>
              <span>{getDate(new Date(time))} {getTime(new Date(time))}</span>
            </span>
            <span
              className="text-info"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                "height": "22px",
                "fontFamily": "PingFangSC",
                "fontWeight": 400,
                "fontSize": "16px",
                "color": "#757D8B",
                "letterSpacing": "0"
              }}
            >
              <span>{t('common.size')}：{size || '-'}</span>
              <span title={t('common.uploadDirectory')}>{dir == 'default' ? t('common.defaultDirectory') : dir}</span>
            </span>
          </div>
        )}
        <hr
          style={{
            marginTop: '14px',
            marginBottom: '14px',
            "borderColor": "#e6eaf0",
            "borderTopWidth": "0px"
          }}
        />
        <div
          className="links"
          aria-disabled={isTencentReviewPending}
          style={isTencentReviewPending ? {
            opacity: 0.4,
            pointerEvents: 'none',
            userSelect: 'none',
          } : undefined}
        >
          <div
            className="first-line"
            style={{
              marginBottom: '8px'
            }}
          >
            <Space split={<Divider type="vertical"/>} size={8} style={{"display": "flex", "width": "100%", "justifyContent": "space-between"}}>

              <Dropdown trigger={['click', 'hover']} open={linkMenuOpen} onOpenChange={setLinkMenuOpen}
                menu={{items: getLinkItems, onClick: () => setLinkMenuOpen(false)}}>
                <span style={{whiteSpace: 'nowrap', color: '#2095f2', textDecoration: 'underline'}} className={getActionClassName('link', 'link')}>
                  {t('common.link')}
                </span>
              </Dropdown>
              {shouldShowOverwriteReupload && (
                <span
                  className={getActionClassName('reupload', 'link')}
                  onClick={handleReuploadClick}
                  style={reuploading ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
                >
                  {t('uploadItem.reupload')}
                </span>
              )}

              {isVideoFile && (
                <span
                  className={getActionClassName('link.video-to-audio', 'link')}
                  onClick={() => navigate('/video-to-audio')}
                >
                  {t('uploadItem.audioLink')}
                </span>
              )}

              {canUseCustomDomain ? (
                <span
                  className={getActionClassName('html-custom-domain', 'link')}
                  onClick={openCustomDomainModal}
                >
                  {t('htmlCustomDomain.action')}
                </span>
              ) : canUseMufyShortLink ? (
                <Dropdown trigger={['hover']} menu={{items: mufyImageLinkItems}}>
                  <span
                    className={getActionClassName('mufy', 'link')}
                    onClick={openMufyLink}
                  >
                    {t('common.mufyLink')}
                  </span>
                </Dropdown>
              ) : canUseMufyDirectMediaLink ? (
                <span
                  className={getActionClassName('mufy', 'link')}
                  onClick={openMufyLink}
                >
                  {t('common.mufyLink')}
                </span>
              ) : null}
              {
                isZip(fileName) && (
                  <>
                    <span
                      className={getActionClassName('extract', 'link')}
                      onClick={() => {
                        const unzipLoadingKey = 'unzipLoadingKey';
                        message.info({
                          content: t('uploadItem.extracting'),
                          key: unzipLoadingKey
                        })
                        const decodedUrl = safeDecode(url);
                        const objectKey = url.substring(decodedUrl.indexOf('autoupload/'));
                        fetch(`/img/unzip-list?objectKey=${objectKey}`).then(res => res.json()).then(res => {
                          message.destroy(unzipLoadingKey);
                          const files = (res.data || []).map((file: {objectKey: string, size: string, url?: string;}) => {
                            const newObjectKey = file.objectKey?.replace(safeDecode(objectKey) + '/', '');
                            return {
                              objectKey: newObjectKey,
                              finalLink: file.url,
                              size: file.size
                            }
                          });
                          Modal.info({
                            title: t('uploadItem.extractContent'),
                            width: '70%',
                            content: (
                              <div
                                className={"unzip-list-ctn"}
                                style={{
                                  marginRight: '34px'
                                }}
                              >
                                <p className={"url"}>
                                  <a href={url} target={'_blank'}>{decodedUrl}</a>
                                </p>
                                <div className="file-list">
                                  <ul
                                    style={{
                                      paddingLeft: '15px',
                                      maxHeight: 'calc(100vh - 300px)',
                                      overflow: 'auto'
                                  }}
                                  >
                                    {
                                      files.map(({objectKey, size, finalLink}: {objectKey: string, size: string, finalLink?: string;}) => {
                                        return (
                                          <li key={objectKey}>
                                            <a href={finalLink || `${url}/${objectKey}`} target={'_blank'}>{finalLink || safeDecode(objectKey)}</a>
                                            &nbsp;
                                            <Tag
                                              color="magenta"
                                              icon={
                                                <CopyOutlined
                                                  style={{
                                                    cursor: 'pointer',
                                                  }}
                                                  onClick={() => {
                                                    copyToClipboard(finalLink || `${url}/${objectKey}`, t('common.linkCopied'));
                                                  }}
                                                />
                                              }
                                            >
                                              {size}
                                            </Tag>
                                          </li>
                                        )
                                      })
                                    }
                                  </ul>
                                </div>
                              </div>
                            )
                          })
                        })
                      }}
                    >
                    {t('uploadItem.extract')}
                  </span>
                  </>
                )
              }
              {
                isImg(fileName) && (
                  <>
                    <Dropdown
                      trigger={['click', 'hover']}
                      menu={{
                        items: photoProcessItems,
                        triggerSubMenuAction: isMobile ? 'click' : 'hover',
                      }}
                    >
                      <span style={{whiteSpace: 'nowrap', color: '#2095f2', textDecoration: 'underline'}} className={getActionClassName('processing', 'link')}>
                        {t('common.photoProcessing')}
                      </span>
                    </Dropdown>
                  </>
                )
              }
              {canUseTextOnlineEdit && (
                canUseHtmlVisualEdit ? (
                  <Dropdown trigger={['click', 'hover']} menu={{items: htmlEditItems}}>
                    <span className={getActionClassName('edit', 'link')}>
                      {t('textEdit.button')}
                    </span>
                  </Dropdown>
                ) : (
                  <span
                    className={getActionClassName('edit', 'link')}
                    onClick={() => setTextEditModalOpen(true)}
                  >
                    {t('textEdit.button')}
                  </span>
                )
              )}
            </Space>
          </div>
          <div
            className="second-line"
          >
            <Space split={<Divider type="vertical"/>} size={8} style={{"display": "flex", "width": "100%", "justifyContent": "space-between"}}>
            <span
              className={getActionClassName('access', 'link')}
              onClick={() => {
                window.open(url, '_blank');
              }}
            >
              {t('common.access')}
            </span>
              <span
                className={getActionClassName('qr-code', 'link')}
                onClick={openQrCodeModal}
              >
                {t('common.qrCode')}
              </span>
              <span
                className={getActionClassName('short-url', 'link')}
                onClick={() => {
                  setShortUrlModalMode('short');
                  setShortUrlModalOpen(true);
                }}
              >
              {t('common.shortUrl')}
            </span>
              <Dropdown trigger={['click', 'hover']} menu={{items: moreItems}}>
                <span style={{whiteSpace: 'nowrap'}} className={getActionClassName('more', 'link')}>
                    {t('common.more')}
                </span>
              </Dropdown>
              {/*  <span*/}
              {/*    style={{display: 'none'}}*/}
              {/*    className={'link dlt-link'}*/}
              {/*    onClick={() => {*/}
              {/*    }}*/}
              {/*  >*/}
              {/*  删除*/}
              {/*</span>*/}
            </Space>
          </div>
        </div>
        {canUseOverwriteReupload && (
          <input
            ref={reuploadInputRef}
            type="file"
            accept={currentReuploadPolicy?.accept || ''}
            style={{ display: 'none' }}
            onChange={handleReuploadChange}
          />
        )}
      </div>
      <ShortUrlModal
        open={shortUrlModalOpen}
        onCancel={() => setShortUrlModalOpen(false)}
        url={url}
        mode={shortUrlModalMode}
        imageLongLinkHost={shortUrlModalMode === 'mufy' ? mufyImageLongLinkHost : ''}
        recommendOverseasLongLink={shortUrlModalMode === 'mufy' && mufyRecommendOverseasLongLink}
      />
      <Modal
        title={t('uploadItem.qrCodeTitle')}
        open={qrCodeModalOpen}
        onCancel={closeQrCodeModal}
        width={460}
        className="qr-code-modal"
        footer={qrCaptionEditing ? [
          <Button key="cancel" onClick={cancelQrCaptionEditing}>
            {t('common.cancel')}
          </Button>,
        ] : [
          <Button key="close" onClick={closeQrCodeModal}>
            {t('common.close')}
          </Button>,
        ]}
      >
        <div className="qr-code-modal-content" data-testid="qr-code-modal">
          {qrCaption.position === 'above' && renderQrCaption()}
          <div className="qr-code-preview">
            <div className="qr-code-styled-preview" ref={qrStylePreviewRef} data-testid="qr-code-styled-preview" />
          </div>
          {qrCaption.position === 'below' && renderQrCaption()}
          <div className="qr-code-style-actions">
            <label className="qr-code-style-preset-field">
              <span>{t('uploadItem.qrCodeStylePreset')}</span>
              <Select
                aria-label={t('uploadItem.qrCodeStylePreset')}
                value={qrStyleSettings.preset}
                onChange={applyQrStylePreset}
                options={([
                  ['classic', t('uploadItem.qrCodePresetClassic')],
                  ['ocean', t('uploadItem.qrCodePresetOcean')],
                  ['jade', t('uploadItem.qrCodePresetJade')],
                  ['sunset', t('uploadItem.qrCodePresetSunset')],
                  ['graphite', t('uploadItem.qrCodePresetGraphite')],
                ] as Array<[QrStylePreset, string]>).map(([value, label]) => ({value, label}))}
              />
            </label>
            <Button onClick={() => setQrStyleAdvancedOpen((open) => !open)}>
              {qrStyleAdvancedOpen ? t('uploadItem.qrCodeStyleBasic') : t('uploadItem.qrCodeStyleAdvanced')}
            </Button>
            <Button icon={<DownloadOutlined />} onClick={downloadStyledQrCode}>
              {t('uploadItem.qrCodeDownload')}
            </Button>
          </div>
          {qrStyleAdvancedOpen && (
            <div className="qr-code-style-panel">
              <div className="qr-code-caption-position-field">
                <span>{t('uploadItem.qrCodeCaptionPosition')}</span>
                <div
                  className="qr-code-caption-position-controls"
                  role="group"
                  aria-label={t('uploadItem.qrCodeCaptionPosition')}
                >
                  <Button
                    aria-label={t('uploadItem.qrCodeCaptionPositionAbove')}
                    type={qrCaption.position === 'above' ? 'primary' : 'default'}
                    onClick={() => setQrCaption((current) => ({...current, position: 'above'}))}
                  >
                    {t('uploadItem.qrCodeCaptionPositionAbove')}
                  </Button>
                  <Button
                    aria-label={t('uploadItem.qrCodeCaptionPositionBelow')}
                    type={qrCaption.position === 'below' ? 'primary' : 'default'}
                    onClick={() => setQrCaption((current) => ({...current, position: 'below'}))}
                  >
                    {t('uploadItem.qrCodeCaptionPositionBelow')}
                  </Button>
                </div>
              </div>
              <div className="qr-code-style-row">
                <label className="qr-code-style-color-field">
                  <span>{t('uploadItem.qrCodeForeground')}</span>
                  <input
                    aria-label={t('uploadItem.qrCodeForeground')}
                    type="color"
                    value={qrStyleSettings.foregroundColor}
                    onChange={(event) => setQrStyleSettings((current) => ({
                      ...current,
                      preset: 'classic',
                      foregroundColor: event.target.value,
                    }))}
                  />
                </label>
                <label className="qr-code-style-color-field">
                  <span>{t('uploadItem.qrCodeBackground')}</span>
                  <input
                    aria-label={t('uploadItem.qrCodeBackground')}
                    type="color"
                    value={qrStyleSettings.backgroundColor}
                    onChange={(event) => setQrStyleSettings((current) => ({
                      ...current,
                      preset: 'classic',
                      backgroundColor: event.target.value,
                    }))}
                  />
                </label>
                <label className="qr-code-style-color-field">
                  <span>{t('uploadItem.qrCodeGradient')}</span>
                  <input
                    aria-label={t('uploadItem.qrCodeGradient')}
                    type="color"
                    disabled={!qrStyleSettings.useGradient}
                    value={qrStyleSettings.gradientColor}
                    onChange={(event) => setQrStyleSettings((current) => ({
                      ...current,
                      preset: 'classic',
                      gradientColor: event.target.value,
                    }))}
                  />
                </label>
                <label className="qr-code-style-switch">
                  <input
                    aria-label={t('uploadItem.qrCodeGradientEnabled')}
                    type="checkbox"
                    checked={qrStyleSettings.useGradient}
                    onChange={(event) => setQrStyleSettings((current) => ({
                      ...current,
                      preset: 'classic',
                      useGradient: event.target.checked,
                    }))}
                  />
                  <span>{t('uploadItem.qrCodeGradientEnabled')}</span>
                </label>
              </div>
              <div className="qr-code-style-row">
                <label className="qr-code-style-select-field">
                  <span>{t('uploadItem.qrCodeDots')}</span>
                  <Select
                    aria-label={t('uploadItem.qrCodeDots')}
                    value={qrStyleSettings.dotsType}
                    onChange={(dotsType: QrDotsType) => setQrStyleSettings((current) => ({...current, preset: 'classic', dotsType}))}
                    options={[
                      {value: 'square', label: t('uploadItem.qrCodeDotsSquare')},
                      {value: 'rounded', label: t('uploadItem.qrCodeDotsRounded')},
                      {value: 'dots', label: t('uploadItem.qrCodeDotsDot')},
                      {value: 'classy-rounded', label: t('uploadItem.qrCodeDotsClassy')},
                      {value: 'extra-rounded', label: t('uploadItem.qrCodeDotsExtraRounded')},
                    ]}
                  />
                </label>
                <label className="qr-code-style-select-field">
                  <span>{t('uploadItem.qrCodeEyeOuter')}</span>
                  <Select
                    aria-label={t('uploadItem.qrCodeEyeOuter')}
                    value={qrStyleSettings.cornerSquareType}
                    onChange={(cornerSquareType: QrCornerSquareType) => setQrStyleSettings((current) => ({...current, preset: 'classic', cornerSquareType}))}
                    options={[
                      {value: 'square', label: t('uploadItem.qrCodeDotsSquare')},
                      {value: 'rounded', label: t('uploadItem.qrCodeDotsRounded')},
                      {value: 'dots', label: t('uploadItem.qrCodeDotsDot')},
                      {value: 'extra-rounded', label: t('uploadItem.qrCodeDotsExtraRounded')},
                    ]}
                  />
                </label>
                <label className="qr-code-style-select-field">
                  <span>{t('uploadItem.qrCodeEyeInner')}</span>
                  <Select
                    aria-label={t('uploadItem.qrCodeEyeInner')}
                    value={qrStyleSettings.cornerDotType}
                    onChange={(cornerDotType: QrCornerDotType) => setQrStyleSettings((current) => ({...current, preset: 'classic', cornerDotType}))}
                    options={[
                      {value: 'square', label: t('uploadItem.qrCodeDotsSquare')},
                      {value: 'rounded', label: t('uploadItem.qrCodeDotsRounded')},
                      {value: 'dot', label: t('uploadItem.qrCodeDotsDot')},
                      {value: 'extra-rounded', label: t('uploadItem.qrCodeDotsExtraRounded')},
                    ]}
                  />
                </label>
                <label className="qr-code-style-select-field">
                  <span>{t('uploadItem.qrCodeMargin')}</span>
                  <Select
                    aria-label={t('uploadItem.qrCodeMargin')}
                    value={qrStyleSettings.margin}
                    onChange={(margin) => setQrStyleSettings((current) => ({...current, preset: 'classic', margin}))}
                    options={[0, 8, 16, 24, 32].map((margin) => ({value: margin, label: `${margin}px`}))}
                  />
                </label>
              </div>
              <div className="qr-code-logo-actions">
                <input
                  ref={qrLogoInputRef}
                  className="qr-code-logo-input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleQrLogoChange}
                />
                <Button icon={<UploadOutlined />} onClick={() => qrLogoInputRef.current?.click()}>
                  {t('uploadItem.qrCodeUploadLogo')}
                </Button>
                {qrStyleSettings.logoUrl && (
                  <Button danger icon={<DeleteOutlined />} onClick={removeQrLogo}>
                    {t('uploadItem.qrCodeRemoveLogo')}
                  </Button>
                )}
                <span>{t('uploadItem.qrCodeLogoHint')}</span>
                {qrRemoteLogoLoading && (
                  <span className="qr-code-online-logo-status">
                    {t('uploadItem.qrCodeOnlineLogoLoading')}
                  </span>
                )}
                {!qrRemoteLogoLoading && qrRemoteLogoConfig.enabled && (
                  <div className="qr-code-online-logo-gallery">
                    <span className="qr-code-online-logo-heading">{t('uploadItem.qrCodeOnlineLogo')}</span>
                    {qrRemoteLogoConfig.logoUrls.length === 0 ? (
                      <span className="qr-code-online-logo-status">
                        {t('uploadItem.qrCodeOnlineLogoEmpty')}
                      </span>
                    ) : (
                      <div className="qr-code-online-logo-grid" role="group" aria-label={t('uploadItem.qrCodeOnlineLogo')}>
                        {qrRemoteLogoConfig.logoUrls.map((configuredLogoUrl, index) => {
                          const logoUrl = resolveQrCodeOnlineLogoUrl(configuredLogoUrl);
                          if (qrRemoteLogoFailedUrls.includes(logoUrl)) {
                            return null;
                          }
                          const optionLabel = `${t('uploadItem.qrCodeOnlineLogoOption')} ${index + 1}`;
                          return (
                            <Button
                              key={logoUrl}
                              aria-label={optionLabel}
                              className={`qr-code-online-logo-option${qrStyleSettings.logoUrl === logoUrl ? ' is-selected' : ''}`}
                              data-testid="qr-code-online-logo-option"
                              type="text"
                              onClick={() => void selectQrRemoteLogo(logoUrl)}
                            >
                              <img
                                src={logoUrl}
                                alt={optionLabel}
                                onError={() => setQrRemoteLogoFailedUrls((current) => (
                                  current.includes(logoUrl) ? current : [...current, logoUrl]
                                ))}
                              />
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>
      <ExpireTimeModal
        open={expireTimeModalOpen}
        onCancel={() => setExpireTimeModalOpen(false)}
        recordId={id}
        currentExpireTime={expireTime ? new Date(expireTime) : null}
        onSuccess={() => onRefresh?.()}
        isVip={isVip}
      />
      <EncryptedLinkModal
        open={encryptedLinkModalOpen}
        onCancel={() => setEncryptedLinkModalOpen(false)}
        uploadId={id}
        fileName={fileName}
      />
      {saleModalOpen && <SaleCreateModal open={saleModalOpen} source={saleSource}
        onCancel={() => setSaleModalOpen(false)} onSaved={(link) => { setSaleModalOpen(false); navigate(`/sale-links/${link.id}/codes`); }} />}
      {canUseAiEdit && (
        <AiImageEditModal
          open={aiEditModalOpen}
          uploadRecordId={Number(id)}
          sourceUrl={url}
          sourceFileName={displayFileName}
          isVip={isVip}
          capability={aiEditCapability as AiImageEditCapability}
          onCancel={() => setAiEditModalOpen(false)}
          onSuccess={() => onRefresh?.()}
        />
      )}
      {canUseTextOnlineEdit && (
        <TextEditModal
          open={textEditModalOpen}
          uploadId={id}
          fileName={displayFileName}
          onCancel={() => setTextEditModalOpen(false)}
          onSaved={() => onRefresh?.()}
        />
      )}
      {isLoggedIn && <PermanentSaveModal open={permanentSaveOpen} uploadRecordId={Number(id)}
        fileName={displayFileName} storageType={storageType} expireTime={expireTime}
        application={permanentSaveState}
        onClose={() => { setPermanentSaveOpen(false); onRefresh?.(); }}
        onSubmitted={request => { setPermanentSaveState(request); }} />}
      <Modal
        title={t('uploadItem.remarkTitle')}
        open={remarkModalOpen}
        onCancel={() => setRemarkModalOpen(false)}
        onOk={isVip ? submitRemark : undefined}
        confirmLoading={remarkSaving}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        footer={!isVip ? (
          <Button onClick={() => setRemarkModalOpen(false)}>
            {t('common.close')}
          </Button>
        ) : undefined}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {!isVip && (
            <Alert
              type="warning"
              showIcon
              message={t('uploadItem.remarkVipOnly')}
            />
          )}
          <Input
            value={remarkInput}
            disabled={!isVip || remarkSaving}
            placeholder={t('uploadItem.remarkPlaceholder')}
            onChange={(event) => setRemarkInput(
              limitCodePoints(event.target.value, MAX_REMARK_CODE_POINTS)
            )}
          />
          <div style={{ color: '#8c8c8c', fontSize: '12px', textAlign: 'right' }}>
            {Array.from(remarkInput).length}/{MAX_REMARK_CODE_POINTS}
          </div>
          {isVip && (
            <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
              {t('uploadItem.remarkClearHint')}
            </div>
          )}
        </Space>
      </Modal>
      <Modal
        title={t('uploadItem.moveDirectory')}
        open={directoryMoveModalOpen}
        onCancel={() => setDirectoryMoveModalOpen(false)}
        onOk={submitDirectoryMove}
        confirmLoading={directoryMoveLoading}
        okText={t('batchActions.confirmMoveDirectory')}
        cancelText={t('common.cancel')}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>{t('uploadItem.moveDirectoryDescription', { fileName: displayFileName })}</div>
          <TreeSelect
            style={{ width: '100%' }}
            value={directoryMoveTarget || undefined}
            onChange={(value) => setDirectoryMoveTarget(typeof value === 'string' ? value : '')}
            placeholder={t('batchActions.moveDirectoryTargetPlaceholder')}
            loading={directoryListLoading}
            dropdownClassName="dir-tree-switcher-right"
            treeData={directoryMoveTreeData}
            treeDefaultExpandAll
            showSearch
            treeNodeFilterProp="title"
          />
        </Space>
      </Modal>
      <Modal
        title={t('uploadItem.switchDomainTitle')}
        open={domainSwitchModalOpen}
        onCancel={() => setDomainSwitchModalOpen(false)}
        onOk={submitDomainSwitch}
        confirmLoading={domainSwitchSaving}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        okButtonProps={{
          disabled: !domainSwitchTarget,
        }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>{t('uploadItem.switchDomainDescription', { fileName: displayFileName })}</div>
          <Alert
            type="info"
            showIcon
            message={t('uploadItem.switchDomainCurrent', { domain: getCurrentUrlHost() || '-' })}
          />
          <Select
            style={{ width: '100%' }}
            value={domainSwitchTarget || undefined}
            onChange={setDomainSwitchTarget}
            placeholder={t('uploadItem.switchDomainPlaceholder')}
            loading={domainSwitchLoading}
            disabled={domainSwitchSaving}
            options={domainSwitchOptions}
            showSearch
            optionFilterProp="label"
          />
        </Space>
      </Modal>
      {canUseCustomDomain && (
        <Modal
          title={t('htmlCustomDomain.title')}
          open={customDomainModalOpen}
          onCancel={() => setCustomDomainModalOpen(false)}
          footer={[
            <Button key="clear" danger disabled={!isVip || !customDomain} loading={customDomainSaving} onClick={() => submitCustomDomain('')}>
              {t('htmlCustomDomain.clear')}
            </Button>,
            <Button key="cancel" onClick={() => setCustomDomainModalOpen(false)} disabled={customDomainSaving}>
              {t('common.cancel')}
            </Button>,
            <Button key="save" type="primary" disabled={!isVip || customDomainPolicyLoading || !selectedCustomDomainSuffix || customDomainPrefixHasDot} loading={customDomainSaving} onClick={() => submitCustomDomain(customDomainPrefixInput)}>
              {t('common.save')}
            </Button>,
          ]}
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert
              type={isVip ? 'info' : 'warning'}
              showIcon
              message={t('htmlCustomDomain.vipOnly')}
              description={t('htmlCustomDomain.dnsHint')}
            />
            <Input
              value={customDomainPrefixInput}
              onChange={(event) => setCustomDomainPrefixInput(event.target.value)}
              disabled={!isVip || customDomainSaving || customDomainPolicyLoading}
              placeholder={t('htmlCustomDomain.placeholder')}
              status={customDomainPrefixHasDot ? 'error' : undefined}
              addonAfter={
                selectedCustomDomainSuffix ? (
                  <Select
                    value={selectedCustomDomainSuffix}
                    onChange={setSelectedCustomDomainSuffix}
                    disabled={!isVip || customDomainSaving || customDomainPolicyLoading}
                    style={{ minWidth: 132 }}
                    options={customDomainSuffixes.map((suffix) => ({
                      value: suffix,
                      label: `.${suffix}`,
                    }))}
                  />
                ) : t('htmlCustomDomain.suffixLoading')
              }
            />
            {customDomainPrefixHasDot && (
              <Alert type="error" showIcon message={t('htmlCustomDomain.prefixInvalid')} />
            )}
            {customDomain && (
              <a
                href={getCustomDomainHref(customDomain)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('htmlCustomDomain.openCurrent', { domain: customDomain })}
              </a>
            )}
          </Space>
        </Modal>
      )}
    </div>
  );
}

export default UploadItem
